import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Modal, Button, Input, message, Typography } from "antd";
import AnalyticsTopBar from "../components/AnalyticsTopBar";
import TimeRangeFilters from "../components/TimeRangeFilters";
import InsightsChart from "../components/InsightsChart";
import InsightsDataTable from "../components/InsightsDataTable";
import MetricCards from "../components/MetricCards";
import FunnelVisualization from "../components/FunnelVisualization";
import FunnelDataTable from "../components/FunnelDataTable";
import FlowVisualization from "../components/FlowVisualization";
import RetentionVisualization from "../components/RetentionVisualization";
import RetentionMetricTable from "../components/RetentionMetricTable";
import AddToBoardDropdown from "../components/AddToBoardDropdown";
import AddContentBlock from "../components/AddContentBlock";
import RichTextToolbar from "../components/RichTextToolbar";
import ConfirmModal from "../components/ConfirmModal";
import { cryptiqueAnalyticsTheme as baseTheme } from "../theme";
import { useBoard } from "../hooks/useBoards";
import { useCards } from "../hooks/useInsights";
import * as analyticsApi from "../api/analyticsApi";
import { Plus, Loader2, MoreVertical } from "lucide-react";
import Icon from "../../../../shared/components/Icon";
import { formatNumber } from "../../../../shared/utils/numberFormatter";

const { Text } = Typography;

const theme = {
  ...baseTheme,
  textPrimary: "#0f172a",
  textSecondary: "#475569",
  textMuted: "#475569",
  primary: "#0f172a",
  accentChart: "#0f172a",
  accentChartMuted: "rgba(15, 23, 42, 0.5)",
};

const DROP_INDICATOR_COLOR = "#6366f1"; /* purple/violet line (Mixpanel-style) */
const DROP_LINE_WIDTH_PX = 3;
const GUTTER_WIDTH_PX = 20; // narrow gutter; simple "+" only
const GUTTER_ACCENT_COLOR = "#4f46e5";
const ROW_RESIZER_WIDTH_PX = 8;
const MIN_CARD_WIDTH_FRACTION = 0.1; // minimum 10% of row width per card
/** Row total width = 1; resize is -y on one card, +y on the other (no normalization). */
const ROW_TOTAL_W = 1;

/** Base row height in pixels; rendered row height = h × BASE_ROW_HEIGHT_PX. Only normalized h is persisted. */
const BASE_ROW_HEIGHT_PX = 260;
const MIN_ROW_H = 0.1;
const MAX_ROW_H = 6;
/** Approximate height of InsightsCard header + divider + margins (my-3) + padding; chart area = rowHeightPx - this */
const INSIGHTS_CARD_HEADER_PX = 105;
const CHART_HEIGHT_MIN_PX = 80;

/**
 * Canonical position: { rowIndex, order, w, h }.
 * Only normalized h (multiplier) is persisted; rendered height = h × BASE_ROW_HEIGHT_PX on load.
 */
function normalizeRowsForSave(orderedRows, cardsById) {
  return orderedRows.flatMap((row, rowIndex) => {
    if (row.length === 0) return [];
    const rowHeight =
      row.length > 0 && typeof row[0]?.position?.h === "number"
        ? row[0].position.h
        : 1;
    if (row.length === 1) {
      const card = row[0];
      const id = card?.id;
      if (id == null) return [];
      return [{ id, position: { rowIndex, order: 0, w: 1, h: rowHeight } }];
    }
    return row.map((card, order) => {
      const id = card?.id;
      if (id == null) return null;
      const w = typeof card?.position?.w === "number" ? card.position.w : 1 / row.length;
      return { id, position: { rowIndex, order, w, h: rowHeight } };
    }).filter(Boolean);
  });
}

/**
 * Defensive on load: assign rowIndex when missing; output canonical { rowIndex, order, w, h }.
 * No width normalization: w is used as-is so separator stays where the user set it.
 * All cards in the same row share the same h (row-level height).
 */
function normalizeCardsOnLoad(cards) {
  if (!cards || !Array.isArray(cards) || cards.length === 0) return cards;
  const hasRowIndex = (c) => typeof c?.position?.rowIndex === "number";
  const withRow = cards.filter(hasRowIndex);
  const withoutRow = cards.filter((c) => !hasRowIndex(c));
  const maxRow = withRow.length > 0
    ? Math.max(...withRow.map((c) => c.position.rowIndex), -1)
    : -1;
  const assigned = withoutRow.map((card, i) => ({
    ...card,
    position: { rowIndex: maxRow + 1 + i, order: 0, w: 1, h: 1 },
  }));
  const all = [...withRow, ...assigned];
  const groups = new Map();
  all.forEach((card) => {
    const r = card.position?.rowIndex ?? 0;
    if (!groups.has(r)) groups.set(r, []);
    groups.get(r).push(card);
  });
  const rows = Array.from(groups.entries())
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([, row]) => row.slice().sort((a, b) => (a.position?.order ?? 0) - (b.position?.order ?? 0)));
  const cardIdToPosition = new Map();
  rows.forEach((row, rowIndex) => {
    const rowHeight =
      row.length > 0 && typeof row[0]?.position?.h === "number"
        ? row[0].position.h
        : 1;
    if (row.length === 1) {
      cardIdToPosition.set(row[0]?.id, { rowIndex, order: 0, w: 1, h: rowHeight });
      return;
    }
    row.forEach((c, order) => {
      const w = typeof c?.position?.w === "number" ? c.position.w : 1 / row.length;
      cardIdToPosition.set(c?.id, { rowIndex, order, w, h: rowHeight });
    });
  });
  return cards.map((card) => {
    const pos = cardIdToPosition.get(card?.id);
    if (!pos) return card;
    return { ...card, position: pos };
  });
}

/**
 * Vertical separator between cards in a row; drag to adjust width (smooth local updates, persist on mouseup like Media resizer).
 */
function RowWidthResizer({ onResizeStart, onResize, onResizeEnd, disabled }) {
  const [resizing, setResizing] = useState(false);
  const prevXRef = useRef(0);

  const handleMouseDown = useCallback(
    (e) => {
      if (disabled) return;
      e.preventDefault();
      prevXRef.current = e.clientX;
      onResizeStart?.();
      setResizing(true);
    },
    [disabled, onResizeStart]
  );

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const deltaPx = e.clientX - prevXRef.current;
      prevXRef.current = e.clientX;
      onResize?.(deltaPx);
    };
    const onUp = () => {
      onResizeEnd?.();
      setResizing(false);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [resizing, onResize, onResizeEnd]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      onMouseDown={handleMouseDown}
      className="flex-shrink-0 flex items-center justify-center cursor-col-resize hover:bg-black/10 active:bg-black/20 transition-colors group/resizer"
      style={{
        width: ROW_RESIZER_WIDTH_PX,
        minWidth: ROW_RESIZER_WIDTH_PX,
        backgroundColor: resizing ? "rgba(79, 70, 229, 0.2)" : "transparent",
      }}
      title="Drag to resize"
    >
      <div
        className="w-0.5 h-8 rounded-full opacity-0 group-hover/resizer:opacity-60 transition-opacity"
        style={{ backgroundColor: GUTTER_ACCENT_COLOR }}
      />
    </div>
  );
}

const ROW_HEIGHT_RESIZER_HEIGHT_PX = 8;

/**
 * Horizontal bar below a row; drag vertically to adjust row height.
 * Same pattern as RowWidthResizer: document move/up, onResize(deltaPx) every mousemove.
 */
function RowHeightResizer({ onResizeStart, onResize, onResizeEnd, disabled }) {
  const [resizing, setResizing] = useState(false);
  const prevYRef = useRef(0);

  const handleMouseDown = useCallback(
    (e) => {
      if (disabled) return;
      e.preventDefault();
      prevYRef.current = e.clientY;
      onResizeStart?.();
      setResizing(true);
    },
    [disabled, onResizeStart]
  );

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const deltaPx = e.clientY - prevYRef.current;
      prevYRef.current = e.clientY;
      onResize?.(deltaPx);
    };
    const onUp = () => {
      onResizeEnd?.();
      setResizing(false);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [resizing, onResize, onResizeEnd]);

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      onMouseDown={handleMouseDown}
      className="flex-shrink-0 w-full flex items-center justify-center cursor-ns-resize hover:bg-black/10 active:bg-black/20 transition-colors group/resizer"
      style={{
        height: ROW_HEIGHT_RESIZER_HEIGHT_PX,
        minHeight: ROW_HEIGHT_RESIZER_HEIGHT_PX,
        backgroundColor: resizing ? "rgba(79, 70, 229, 0.2)" : "transparent",
      }}
      title="Drag to resize"
    >
      <div
        className="h-0.5 w-12 rounded-full opacity-0 group-hover/resizer:opacity-60 transition-opacity"
        style={{ backgroundColor: GUTTER_ACCENT_COLOR }}
      />
    </div>
  );
}

/**
 * Row wrapper: action gutter (left) + flex area for cards. Supports multiple cards per row (side-by-side).
 * Gutter shows vertical line and "+" button only on row hover.
 * When used with a single draggable row, receives provided/snapshot and applies to root.
 */
function BoardRow({ children, rowIndex: rowId, rowArrayIndex, insertIndex, onAddSelect, provided, snapshot, rowContainerRef, showLineAbove, showLineBelow, rowClassName }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const anchorRef = useRef(null);
  const rowIdAtOpenRef = useRef(null);
  const isDragging = snapshot?.isDragging ?? false;

  const handleOpenDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Capture this row's canonical id (rowId) so we add to this exact row regardless of re-renders or closure
    rowIdAtOpenRef.current = rowId;
    setDropdownOpen((o) => !o);
  };

  const handleSelect = (id) => {
    setDropdownOpen(false);
    // Use row id captured when "+" was clicked — same row where the + appears
    const idToUse = rowIdAtOpenRef.current;
    onAddSelect?.(id, insertIndex, idToUse);
    rowIdAtOpenRef.current = null;
  };

  const rootProps = provided
    ? {
        ref: provided.innerRef,
        ...provided.draggableProps,
        style: {
          ...provided.draggableProps?.style,
          opacity: isDragging ? 0.5 : 1,
          minHeight: 1,
        },
      }
    : { style: { minHeight: 1 } };

  return (
    <div {...rootProps} className={`group/row flex flex-row w-full rounded-lg transition-colors duration-200 relative ${rowClassName || ""}`.trim()}>
      {showLineAbove && (
        <div
          className="absolute left-0 right-0 top-0 pointer-events-none z-20"
          style={{
            height: DROP_LINE_WIDTH_PX,
            backgroundColor: DROP_INDICATOR_COLOR,
            borderRadius: 1,
          }}
          aria-hidden
        />
      )}
      {/* Cards area: pr-px so last card's right border is not clipped; entire row is drop zone */}
      <div ref={rowContainerRef} className="flex-1 min-w-0 flex flex-row items-stretch pr-px min-h-0">
        {children}
      </div>
      {showLineBelow && (
        <div
          className="absolute left-0 right-0 bottom-0 pointer-events-none z-20"
          style={{
            height: DROP_LINE_WIDTH_PX,
            backgroundColor: DROP_INDICATOR_COLOR,
            borderRadius: 1,
          }}
          aria-hidden
        />
      )}
      {/* "+" outside card section: right gutter, visible on row hover */}
      <div
        className="flex-shrink-0 relative flex items-center justify-center z-10"
        style={{ width: GUTTER_WIDTH_PX, minHeight: "100%", backgroundColor: "inherit" }}
      >
        <div className="absolute inset-0 flex items-start justify-center pt-2 pointer-events-none">
          <div className="relative pointer-events-auto opacity-0 group-hover/row:opacity-100 transition-opacity duration-200">
            <button
              type="button"
              ref={anchorRef}
              onClick={handleOpenDropdown}
              className="w-5 h-5 flex items-center justify-center rounded cursor-pointer hover:bg-black/10 focus:outline-none focus:ring-1 focus:ring-offset-0"
              style={{ color: theme.textMuted }}
              title="Add content to this row"
              aria-label="Add content"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
            {dropdownOpen && (
              <AddToBoardDropdown
                open={dropdownOpen}
                onClose={() => setDropdownOpen(false)}
                anchorRef={anchorRef}
                onSelectType={handleSelect}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SixDotGripIcon({ className, style }) {
  return (
    <span className={className} style={style} aria-hidden>
      <svg width="14" height="18" viewBox="0 0 14 18" fill="currentColor" className="block">
        <circle cx="4" cy="4" r="1.5" />
        <circle cx="10" cy="4" r="1.5" />
        <circle cx="4" cy="9" r="1.5" />
        <circle cx="10" cy="9" r="1.5" />
        <circle cx="4" cy="14" r="1.5" />
        <circle cx="10" cy="14" r="1.5" />
      </svg>
    </span>
  );
}

function BoardWidgetWithHandle({ card, cardIndex, canEdit, onDelete, onEdit, cardContent, showDropIndicatorLeft, showDropIndicatorRight, provided, snapshot, dragHandleProps: dragHandlePropsFromParent }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isStandalone = !!provided;
  const dragHandleProps = dragHandlePropsFromParent ?? provided?.dragHandleProps;

  useEffect(() => {
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) {
      document.addEventListener("click", close);
      return () => document.removeEventListener("click", close);
    }
  }, [menuOpen]);

  const isDragging = snapshot?.isDragging ?? false;

  const rootProps = isStandalone
    ? {
        ref: provided.innerRef,
        ...provided.draggableProps,
        className: "group relative rounded-lg mb-1 w-full h-full transition-colors duration-200",
        style: {
          ...provided.draggableProps.style,
          opacity: isDragging ? 0.5 : 1,
        },
      }
    : { className: "group relative rounded-lg mb-1 w-full h-full transition-colors duration-200" };

  return (
    <div {...rootProps} className={(rootProps.className || "") + " flex flex-col relative"}>
      {/* Vertical line on left: insert card to the left of this card (Mixpanel-style) */}
      {showDropIndicatorLeft && (
        <div
          className="absolute left-0 top-0 bottom-0 pointer-events-none z-20"
          style={{
            width: DROP_LINE_WIDTH_PX,
            backgroundColor: DROP_INDICATOR_COLOR,
            borderRadius: 1,
          }}
          aria-hidden
        />
      )}
      {/* Vertical line on right: insert card to the right of this card (Mixpanel-style) */}
      {showDropIndicatorRight && (
        <div
          className="absolute right-0 top-0 bottom-0 pointer-events-none z-20"
          style={{
            width: DROP_LINE_WIDTH_PX,
            backgroundColor: DROP_INDICATOR_COLOR,
            borderRadius: 1,
          }}
          aria-hidden
        />
      )}
      <div className="relative w-full flex-1 min-h-0 flex flex-col">
        {dragHandleProps && (
          <div
            {...dragHandleProps}
            className="absolute top-[14px] left-[8px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-grab active:cursor-grabbing select-none z-10"
            title="Drag to move"
            style={{ color: theme.textMuted }}
          >
            <SixDotGripIcon className="block" style={{ color: theme.textMuted }} />
          </div>
        )}
        <div className="absolute z-10 right-3 top-3" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="p-1 rounded opacity-80 hover:opacity-100 transition-opacity"
            style={{ color: theme.textMuted }}
            title="More options"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-0.5 py-1 rounded-lg shadow-lg border z-20 min-w-[160px]"
              style={{ backgroundColor: theme.bgRaised, borderColor: theme.border }}
            >
              {canEdit && (
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onEdit(); }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-black/5 flex items-center gap-2"
                  style={{ color: theme.textPrimary }}
                >
                  <Icon name="edit_pencil" size={16} color={theme.textPrimary} /> Edit
                </button>
              )}
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDelete(); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-black/5 flex items-center gap-2"
                style={{ color: "#ef4444" }}
              >
                <Icon name="delete" size={14} /> Delete
              </button>
            </div>
          )}
        </div>
        {cardContent}
      </div>
    </div>
  );
}

const HEADER_DIVIDER_COLOR = "#f3f4f6";

/** Flatten breakdown format for table chart (GroupedFlatTable): one item per (metric, breakdown combo).
 *  Sort by total desc, limit to 50 per metric. Output format: breakdownValues, metricId, fullMetricName, summary.total */
function flattenBreakdownsForTable(resultSeries, limitPerMetric = 50) {
  if (!resultSeries || resultSeries.length === 0) return [];
  const isBreakdownFormat = resultSeries[0].breakdowns && Array.isArray(resultSeries[0].breakdowns) && resultSeries[0].breakdowns.length > 0;
  if (!isBreakdownFormat) return resultSeries;
  const allSeries = [];
  resultSeries.forEach((metricSeries, metricIndex) => {
    if (!metricSeries.breakdowns || !Array.isArray(metricSeries.breakdowns)) return;
    const metricSegments = metricSeries.breakdowns.map((breakdown) => {
      const dataPoints = breakdown.data || [];
      const sum = dataPoints.reduce((acc, point) => acc + (point.value || 0), 0);
      const segmentTotal = breakdown.total ?? sum;
      return {
        metricId: metricSeries.metricId,
        fullMetricName: metricSeries.metricName,
        breakdownKey: breakdown.breakdownKey,
        breakdownValues: breakdown.breakdownValues ?? (breakdown.breakdownKey ? [breakdown.breakdownKey] : []),
        data: breakdown.data || [],
        summary: { total: segmentTotal },
      };
    });
    metricSegments.sort((a, b) => (b.summary?.total ?? 0) - (a.summary?.total ?? 0));
    allSeries.push(...metricSegments.slice(0, limitPerMetric));
  });
  return allSeries;
}

/** Flatten breakdown format into one series per segment (one card per country, city, etc.).
 *  When multiple metrics: apply limit PER METRIC so each metric gets its own top N segments.
 *  Card header shows breakdown value (e.g. Abuja, India/Gurugram); same metric keeps A./B. prefix. */
function flattenBreakdownsForMetricCards(resultSeries, limitPerMetric = 10) {
  if (!resultSeries || resultSeries.length === 0) return resultSeries;
  const isBreakdownFormat = resultSeries[0].breakdowns && Array.isArray(resultSeries[0].breakdowns) && resultSeries[0].breakdowns.length > 0;
  if (!isBreakdownFormat) return resultSeries;
  const allSegments = [];
  resultSeries.forEach((metricSeries, metricIndex) => {
    if (!metricSeries.breakdowns || !Array.isArray(metricSeries.breakdowns)) return;
    const metricIdentifier = String.fromCharCode(65 + metricIndex);
    const segments = metricSeries.breakdowns.map((b) => {
      const breakdownValueLabel = b.breakdownKey || (b.breakdownValues && b.breakdownValues.join(" / ")) || metricSeries.metricName;
      const displayLabel = b.breakdownValues && b.breakdownValues.length > 0 ? b.breakdownValues.join("/") : (b.breakdownKey || breakdownValueLabel);
      return {
        metricId: metricSeries.metricId,
        metricName: displayLabel,
        metricIdentifier,
        displayLabel,
        fullMetricName: metricSeries.metricName,
        originalBreakdownKey: breakdownValueLabel,
        data: b.data || [],
        summary: { total: b.total || 0, average: (b.data || []).reduce((acc, p) => acc + (p.value || 0), 0) / Math.max(1, (b.data || []).length) },
        breakdownKey: b.breakdownKey,
        breakdownValues: b.breakdownValues,
      };
    });
    segments.sort((a, b) => (b.summary?.total ?? 0) - (a.summary?.total ?? 0));
    allSegments.push(...segments.slice(0, limitPerMetric));
  });
  return allSegments;
}

/** Sort and limit breakdowns per metric using saved breakdownLimit/breakdownSortOrder (so board matches editor). */
function sortAndLimitBreakdownsForBoard(resultSeries, definition) {
  if (!resultSeries || resultSeries.length === 0) return resultSeries;
  const isBreakdownFormat = resultSeries[0].breakdowns && Array.isArray(resultSeries[0].breakdowns) && resultSeries[0].breakdowns.length > 0;
  if (!isBreakdownFormat) return resultSeries;
  const showMax = definition?.breakdownShowMax === true;
  const maxLimit = showMax ? 50 : Math.min(Math.max(1, Number(definition?.breakdownLimit) || 10), 50);
  const sortOrder = definition?.breakdownSortOrder || 'average-desc';
  const sortFn = (() => {
    switch (sortOrder) {
      case 'segment-a-z': return (a, b) => (a.breakdownKey || '').localeCompare(b.breakdownKey || '');
      case 'segment-z-a': return (a, b) => (b.breakdownKey || '').localeCompare(a.breakdownKey || '');
      case 'average-asc': return (a, b) => (a.data || []).reduce((s, p) => s + (p.value || 0), 0) / Math.max(1, (a.data || []).length) - (b.data || []).reduce((s, p) => s + (p.value || 0), 0) / Math.max(1, (b.data || []).length);
      case 'average-desc':
      default: return (a, b) => (b.total ?? 0) - (a.total ?? 0);
    }
  })();
  return resultSeries.map((metricSeries) => {
    if (!metricSeries.breakdowns || !Array.isArray(metricSeries.breakdowns)) return metricSeries;
    const sorted = [...metricSeries.breakdowns].sort(sortFn);
    return { ...metricSeries, breakdowns: sorted.slice(0, maxLimit) };
  });
}

function InsightsCard({ card, result, rowHeightPx }) {
  const rawSeries = result?.data?.series;
  const definition = card?.queryDsl?.definition;
  const breakdownLimit = definition?.breakdownShowMax ? 50 : (Math.max(1, Number(definition?.breakdownLimit) || 10));
  const series = rawSeries && Array.isArray(rawSeries) && rawSeries.length > 0 && rawSeries[0].breakdowns && Array.isArray(rawSeries[0].breakdowns)
    ? sortAndLimitBreakdownsForBoard(rawSeries, definition)
    : rawSeries;
  const hasData = result && result.data && Array.isArray(series) && series.length > 0;
  const s0 = series?.[0];
  const summary = s0?.summary;
  const delta = s0?.delta;
  const deltaStr = delta != null
    ? `${delta.percentage >= 0 ? "+" : ""}${Number(delta.percentage).toFixed(1)}%`
    : null;
  // Merge visualization: prefer card.visualization (persisted chart customization) over queryDsl.visualization
  const cardVis = card?.visualization && typeof card.visualization === "object" ? card.visualization : {};
  const dslVis = card?.queryDsl?.visualization && typeof card.queryDsl.visualization === "object" ? card.queryDsl.visualization : {};
  const mergedVis = { ...dslVis, ...cardVis };
  const chartConfig = mergedVis.config && typeof mergedVis.config === "object" ? mergedVis.config : null;
  const chartType = mergedVis.chartType || "line";
  // Pass full result.data plus metrics with axis assignment (from saved queryDsl) so dual Y-axis works
  const defMetrics = card?.queryDsl?.definition?.metrics;
  const metricsWithAxis = Array.isArray(defMetrics)
    ? defMetrics.map((m) => ({ id: m.id, axis: m.axis || "left" }))
    : null;
  const chartData = hasData
    ? { ...result.data, series, metrics: metricsWithAxis }
    : undefined;

  // Chart height from row so content responds to row resize; renderedHeight = h × BASE_ROW_HEIGHT_PX
  const chartHeight =
    typeof rowHeightPx === "number" && rowHeightPx > 0
      ? Math.max(CHART_HEIGHT_MIN_PX, Math.round(rowHeightPx - INSIGHTS_CARD_HEADER_PX))
      : 280;

  // Metadata line: list all metric names and totals for multi-metric, or single metric + delta
  const metadataParts = [];
  if (hasData) {
    if (series.length === 1) {
      metadataParts.push(s0.metricName || (chartType === "line" ? "Linear" : "Count"));
      if (summary?.total != null) metadataParts.push(`Total: ${typeof summary.total === 'number' ? formatNumber(summary.total) : String(summary.total)}`);
      if (deltaStr) metadataParts.push(deltaStr);
    } else {
      metadataParts.push(
        series.map((s) => {
          const total = s.summary?.total;
          return total != null ? `${s.metricName || "Metric"} (Total: ${typeof total === 'number' ? formatNumber(total) : String(total)})` : (s.metricName || "Metric");
        }).join(", ")
      );
    }
  }
  const metadataLine = metadataParts.length > 0 ? metadataParts.join(" • ") : null;

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden w-full" style={{ backgroundColor: "transparent" }}>
      {/* Header: compact padding (left from card pl-6) */}
      <div className="flex-shrink-0" style={{ padding: "8px 16px 8px 0" }}>
        <h3 className="text-sm font-bold truncate" style={{ color: theme.textPrimary }}>
          {card.title || "Untitled"}
        </h3>
        {metadataLine && (
          <p className="text-xs truncate mt-0.5" style={{ color: theme.textMuted }}>
            {metadataLine}
          </p>
        )}
      </div>
      {/* Divider: very light border below header */}
      <div
        className="w-full flex-shrink-0"
        style={{ height: 1, backgroundColor: HEADER_DIVIDER_COLOR }}
        aria-hidden
      />
      {/* Chart body: fills row; chart height derived from row so resize is responsive */}
      <div
        className="flex-1 w-full flex flex-col my-3 min-h-0"
        style={{
          padding: "0 16px 10px 0",
        }}
      >
        {chartType === "metric" && hasData ? (
          <div className="flex-1 w-full min-h-0 overflow-auto">
            <MetricCards
              series={flattenBreakdownsForMetricCards(series, breakdownLimit)}
              seriesColors={chartConfig?.colors}
            />
          </div>
        ) : (
          <>
            {hasData && series.length === 1 && s0?.metricName && chartType !== "metric" && chartType !== "table" && (
              <div className="flex-shrink-0 pt-1 pb-0.5">
                <span className="text-xs" style={{ color: theme.textMuted }}>
                  {s0.metricName}
                </span>
              </div>
            )}
            <div className="flex-1 w-full min-h-0 overflow-auto" style={{ width: "100%" }}>
              {hasData ? (
                chartType === "table" &&
                series[0]?.breakdowns &&
                Array.isArray(series[0].breakdowns) &&
                series[0].breakdowns.length > 0 ? (
                  <InsightsDataTable
                    allSeries={flattenBreakdownsForTable(series, breakdownLimit)}
                    seriesColors={chartConfig?.colors}
                    breakdownProperties={s0?.breakdownProperties}
                    breakdownFormat={true}
                    chartType="table"
                    fullTotalsByMetric={
                      series.reduce((acc, s) => {
                        const id = s.metricId ?? s.metricName;
                        if (id != null && s.summary?.total != null) acc[id] = s.summary.total;
                        return acc;
                      }, {})
                    }
                  />
                ) : (
                  <InsightsChart
                    data={chartData}
                    chartType={chartType}
                    config={chartConfig}
                    height={chartHeight}
                  />
                )
              ) : result && result.error ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-sm" style={{ color: "#ef4444" }}>
                    Error loading data
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-sm" style={{ color: theme.textMuted }}>
                    Loading...
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FunnelCard({ card, result, rowHeightPx }) {
  const steps = result?.data?.steps || [];
  const measuring = card?.queryDsl?.definition?.measuring || "conversion_rate";
  const overallConversionRate =
    steps.length > 1 ? steps[steps.length - 1]?.conversion : steps.length === 1 ? 100 : null;

  if (steps.length === 0) {
    return (
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden w-full" style={{ backgroundColor: "transparent" }}>
        <div className="flex items-center justify-between py-1.5 pr-4" style={{ paddingLeft: 0 }}>
          <h3 className="text-sm font-medium truncate" style={{ color: theme.textMuted }}>{card?.title || "Funnel"}</h3>
        </div>
        <div className="pr-4 pb-3 flex-1 min-h-0 my-3 flex flex-col items-center justify-center text-center" style={{ paddingLeft: 0, color: theme.textMuted }}>
          <p className="text-sm">Funnel report</p>
          <p className="text-xs mt-1">Configure in editor</p>
        </div>
      </div>
    );
  }

  const stepsWithMeasuring = steps.map((step) => ({
    ...step,
    measuring: step.measuring || measuring,
  }));

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-auto w-full" style={{ backgroundColor: "transparent" }}>
      <div className="flex items-center justify-between py-1.5 pr-4 flex-shrink-0" style={{ paddingLeft: 0 }}>
        <h3 className="text-sm font-medium truncate" style={{ color: theme.textMuted }}>{card?.title || "Funnel"}</h3>
      </div>
      <div className="pr-4 pb-3 flex-1 min-h-0 overflow-auto" style={{ paddingLeft: 0 }}>
        <FunnelVisualization
          steps={stepsWithMeasuring}
          measuring={measuring}
          overallConversionRate={overallConversionRate}
        />
        <div className="mt-6">
          <FunnelDataTable steps={stepsWithMeasuring} measuring={measuring} />
        </div>
      </div>
    </div>
  );
}

function FlowCard({ card, result, rowHeightPx }) {
  // result is the flow engine response: { nodes, links, anchors, totalUsers }
  const flowData = result?.data?.nodes ? result.data : result?.data?.data || null;
  const chartHeight = rowHeightPx ? Math.max(200, rowHeightPx - 60) : 280;

  if (!flowData || !flowData.nodes?.length) {
    return (
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden w-full" style={{ backgroundColor: "transparent" }}>
        <div className="flex items-center justify-between py-1.5 pr-4" style={{ paddingLeft: 0 }}>
          <h3 className="text-sm font-medium truncate" style={{ color: theme.textMuted }}>{card?.title || "Flow"}</h3>
        </div>
        <div className="pr-4 pb-3 flex-1 min-h-0 flex flex-col items-center justify-center text-center" style={{ paddingLeft: 0, color: theme.textMuted }}>
          <p className="text-sm">Feature Flow</p>
          <p className="text-xs mt-1">Configure in editor</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-auto w-full" style={{ backgroundColor: "transparent" }}>
      <div className="flex items-center justify-between py-1.5 pr-4 flex-shrink-0" style={{ paddingLeft: 0 }}>
        <h3 className="text-sm font-medium truncate" style={{ color: theme.textMuted }}>{card?.title || "Flow"}</h3>
      </div>
      <div className="pr-4 pb-2 flex-1 min-h-0" style={{ paddingLeft: 0 }}>
        <FlowVisualization data={flowData} height={chartHeight} />
      </div>
    </div>
  );
}

function RetentionCard({ card, result, rowHeightPx }) {
  const intervals = result?.data?.intervals || [];
  const cohorts = result?.data?.cohorts || [];
  const weightedAverage = result?.data?.weightedAverage || null;
  const breakdowns = result?.data?.breakdowns || [];
  const metric = card?.queryDsl?.definition?.metric || "retention_rate";
  const config = card?.visualization?.config || {};
  const chartType = card?.visualization?.chartType || "retention-curve";
  const startEvent = card?.queryDsl?.definition?.startEvent || "Start Event";
  const returnEvent = card?.queryDsl?.definition?.returnEvent || "Return Event";

  if (intervals.length === 0 || cohorts.length === 0) {
    return (
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden w-full" style={{ backgroundColor: "transparent" }}>
        <div className="flex items-center justify-between py-1.5 pr-4" style={{ paddingLeft: 0 }}>
          <h3 className="text-sm font-medium truncate" style={{ color: theme.textMuted }}>{card?.title || "Retention"}</h3>
        </div>
        <div className="pr-4 pb-3 flex-1 min-h-0 my-3 flex flex-col items-center justify-center text-center" style={{ paddingLeft: 0, color: theme.textMuted }}>
          <p className="text-sm">Retention report</p>
          <p className="text-xs mt-1">Configure in editor</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-auto w-full" style={{ backgroundColor: "transparent" }}>
      <div className="flex items-center justify-between py-1.5 pr-4 flex-shrink-0" style={{ paddingLeft: 0 }}>
        <h3 className="text-sm font-medium truncate" style={{ color: theme.textMuted }}>{card?.title || "Retention"}</h3>
      </div>
      <div className="pr-4 pb-3 flex-1 min-h-0 overflow-auto" style={{ paddingLeft: 0 }}>
        {chartType === "metric" ? (
          <div className="w-full">
            {(() => {
              const idx =
                typeof config?.retentionLineIntervalIndex === "number"
                  ? config.retentionLineIntervalIndex
                  : 0;
              const safeIdx =
                Array.isArray(intervals) && intervals.length > 0
                  ? Math.min(Math.max(0, idx), intervals.length - 1)
                  : 0;
              const groupLabel = intervals?.[safeIdx] || "<1 Day";
              const values = weightedAverage?.values || [];
              const selected = values[safeIdx] ?? values[0];
              const rawNum = selected?.value ?? selected;

              let displayValue = metric === "retention_rate" ? "0%" : "0";
              if (metric === "retention_rate") {
                const num = Number(rawNum);
                const pct = Number.isFinite(num) ? (num <= 1 ? num * 100 : num) : 0;
                displayValue = `${pct.toFixed(2)}%`;
              } else {
                const users = Number(rawNum);
                displayValue = Number.isFinite(users) ? formatNumber(users) : "0";
              }

              return (
                <>
                  <div
                    className="w-full flex items-center justify-center rounded-xl mb-4"
                    style={{
                      minHeight: "180px",
                      backgroundColor: "rgba(124, 58, 237, 0.10)",
                      border: `1px solid ${theme.border}`,
                    }}
                  >
                    <div className="text-center">
                      <div className="text-xs mb-1" style={{ color: theme.textMuted }}>
                        {`${startEvent} then ${returnEvent}`}
                      </div>
                      <div className="text-5xl font-bold" style={{ color: theme.primary }}>
                        {displayValue}
                      </div>
                      <div className="text-xs mt-1" style={{ color: theme.textMuted }}>
                        {metric === "retention_rate" ? "Retention Rate" : "Unique Users"} • {groupLabel}
                      </div>
                    </div>
                  </div>

                  <RetentionMetricTable
                    label={`${startEvent} then ${returnEvent}`}
                    value={displayValue}
                  />
                </>
              );
            })()}
          </div>
        ) : (
          <RetentionVisualization
            intervals={intervals}
            cohorts={cohorts}
            weightedAverage={weightedAverage}
            breakdowns={breakdowns}
            metric={metric}
            config={config}
            chartType={chartType}
            startEventLabel={startEvent}
            returnEventLabel={returnEvent}
          />
        )}
      </div>
    </div>
  );
}

function ReportPlaceholderCard({ card, cardTypeLabel }) {
  return (
    <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: "transparent" }}>
      <div className="flex items-center justify-between py-1.5 pr-4 flex-shrink-0" style={{ paddingLeft: 0 }}>
        <h3 className="text-sm font-medium truncate" style={{ color: theme.textMuted }}>{card?.title || cardTypeLabel}</h3>
      </div>
      <div className="pr-4 pb-3 flex-1 min-h-0 flex flex-col items-center justify-center text-center" style={{ color: theme.textMuted, paddingLeft: 0 }}>
        <p className="text-sm">{cardTypeLabel}</p>
        <p className="text-xs mt-0.5">Configure in editor</p>
      </div>
    </div>
  );
}

// Typography: T1/T2/T3 – no top/bottom margin on text fields to avoid extra yellow/green space
const CARD_TEXT_STYLES = {
  t1: { fontSize: "1.75rem", fontWeight: 700, marginTop: 0, marginBottom: 0 },
  t2: { fontSize: "1.25rem", fontWeight: 600, marginTop: 0, marginBottom: 0 },
  text: { fontSize: "0.875rem", fontWeight: 400, marginTop: 0, marginBottom: 0 },
};

// Reset margin on block elements inside the text card editor (browser wraps content in <p> which has default margin = green space in DevTools)
const TEXT_CARD_EDITOR_CLASS = "board-text-card-editor";

function TextCard({ card, onDelete, onEdit, onSaveContent, inlineEdit = true }) {
  const q = card?.queryDsl && typeof card.queryDsl === "object" ? card.queryDsl : {};
  const savedContent = q.content ?? "";
  const savedTextStyle = q.textStyle ?? "text";
  const [content, setContent] = useState(savedContent);
  const [textStyle, setTextStyle] = useState(savedTextStyle);
  const [showToolbar, setShowToolbar] = useState(false);
  const [selectionRect, setSelectionRect] = useState(null);
  const bodyRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const savedRangeRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  useEffect(() => {
    setContent(savedContent);
    setTextStyle(savedTextStyle);
  }, [savedContent, savedTextStyle]);

  useEffect(() => {
    if (bodyRef.current && savedContent !== undefined) {
      if (bodyRef.current.innerHTML !== (savedContent || "")) {
        bodyRef.current.innerHTML = savedContent || "";
      }
    }
  }, [savedContent]);

  const persist = useCallback((payload = {}) => {
    if (!onSaveContent) return;
    const newContent = payload.content !== undefined ? payload.content : (bodyRef.current?.innerHTML ?? content);
    const newTextStyle = payload.textStyle !== undefined ? payload.textStyle : textStyle;
    const prevContent = (q.content ?? "").trim();
    const prevTextStyle = q.textStyle ?? "text";
    if ((newContent || "").trim() !== prevContent || newTextStyle !== prevTextStyle) {
      onSaveContent({ title: "", content: newContent || "", textStyle: newTextStyle });
    }
  }, [onSaveContent, content, textStyle, q.content, q.textStyle]);

  const schedulePersist = useCallback(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(persist, 1000);
  }, [persist]);

  const checkSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !bodyRef.current) return;
    const range = sel.getRangeAt(0);
    if (!bodyRef.current.contains(sel.anchorNode) || !bodyRef.current.contains(sel.focusNode)) return;
    if (range.collapsed) {
      setShowToolbar(false);
      setSelectionRect(null);
      savedRangeRef.current = null;
      return;
    }
    try {
      savedRangeRef.current = range.cloneRange();
      const rect = range.getBoundingClientRect();
      if (rect.width > 0 || rect.height > 0) {
        setSelectionRect({ top: rect.top, left: rect.left, bottom: rect.bottom, width: rect.width, height: rect.height });
        setShowToolbar(true);
      }
    } catch (_) {
      setShowToolbar(false);
      savedRangeRef.current = null;
    }
  }, []);

  const restoreSelection = useCallback(() => {
    const range = savedRangeRef.current;
    if (!range || !bodyRef.current) return;
    try {
      const el = bodyRef.current;
      if (!el.contains(range.startContainer) || !el.contains(range.endContainer)) return;
      el.focus();
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (_) {}
  }, []);

  const handleBodyInput = () => {
    if (bodyRef.current) setContent(bodyRef.current.innerHTML);
    schedulePersist();
  };

  const handleBodyMouseUp = () => {
    setTimeout(checkSelection, 0);
  };

  const handleBodyKeyUp = () => {
    setTimeout(checkSelection, 0);
  };

  const handleBodyBlur = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      setShowToolbar(false);
      setSelectionRect(null);
      savedRangeRef.current = null;
      persist();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    }, 150);
  };

  const cancelBlurHide = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
  }, []);

  const handleToolbarAfterAction = () => {
    setShowToolbar(false);
    setSelectionRect(null);
    savedRangeRef.current = null;
    if (bodyRef.current) setContent(bodyRef.current.innerHTML);
    schedulePersist();
  };

  const handleTextStyleChange = (styleId) => {
    setTextStyle(styleId);
    const html = bodyRef.current?.innerHTML ?? content;
    onSaveContent?.({ title: "", content: html || "", textStyle: styleId });
    setShowToolbar(false);
    setSelectionRect(null);
    savedRangeRef.current = null;
  };

  const bodyStyle = CARD_TEXT_STYLES[textStyle] || CARD_TEXT_STYLES.text;

  if (!inlineEdit) {
    return (
      <div className="flex-1 min-h-0 pt-3 pr-4 flex flex-col overflow-hidden" style={{ backgroundColor: "transparent", paddingLeft: 0 }}>
        <style>{`.${TEXT_CARD_EDITOR_CLASS} p, .${TEXT_CARD_EDITOR_CLASS} div { margin: 0; } .${TEXT_CARD_EDITOR_CLASS} ul, .${TEXT_CARD_EDITOR_CLASS} ol { margin: 0.25em 0; padding-left: 1.25em; } .${TEXT_CARD_EDITOR_CLASS} ul { list-style-type: disc; } .${TEXT_CARD_EDITOR_CLASS} ol { list-style-type: decimal; } .${TEXT_CARD_EDITOR_CLASS} li { margin: 0.125em 0; }`}</style>
        <div
          className={`${TEXT_CARD_EDITOR_CLASS} min-h-[1.75rem] w-full rounded outline-none flex flex-col overflow-auto flex-1`}
          style={{
            color: theme.textSecondary,
            fontFamily: "Raleway, sans-serif",
            padding: 0,
            minHeight: "1.75rem",
            ...bodyStyle,
          }}
          dangerouslySetInnerHTML={{ __html: savedContent ?? "" }}
        />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 pt-3 pr-4 flex flex-col overflow-hidden" style={{ backgroundColor: "transparent", paddingLeft: 0 }}>
      <style>{`.${TEXT_CARD_EDITOR_CLASS} p, .${TEXT_CARD_EDITOR_CLASS} div { margin: 0; } .${TEXT_CARD_EDITOR_CLASS} ul, .${TEXT_CARD_EDITOR_CLASS} ol { margin: 0.25em 0; padding-left: 1.25em; } .${TEXT_CARD_EDITOR_CLASS} ul { list-style-type: disc; } .${TEXT_CARD_EDITOR_CLASS} ol { list-style-type: decimal; } .${TEXT_CARD_EDITOR_CLASS} li { margin: 0.125em 0; }`}</style>
      <div className="flex-1 min-w-0 flex flex-col min-h-0 overflow-hidden">
        {/* Bubble toolbar: only when text is selected */}
        <RichTextToolbar
          editorRef={bodyRef}
          visible={showToolbar}
          selectionRect={selectionRect}
          restoreSelection={restoreSelection}
          onAfterAction={handleToolbarAfterAction}
          onToolbarInteraction={cancelBlurHide}
          onTextStyleChange={handleTextStyleChange}
          currentTextStyle={textStyle}
        />
        {/* Body: fills row height, scrolls when row is short so content responds to resize */}
        <div
          ref={bodyRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleBodyInput}
          onMouseUp={handleBodyMouseUp}
          onKeyUp={handleBodyKeyUp}
          onBlur={handleBodyBlur}
          data-placeholder="Write your text..."
          className={`${TEXT_CARD_EDITOR_CLASS} min-h-[1.75rem] w-full rounded outline-none leading-snug focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 flex flex-col overflow-auto flex-1`}
          style={{
            color: theme.textSecondary,
            fontFamily: "Raleway, sans-serif",
            padding: 0,
            minHeight: "1.75rem",
            ...bodyStyle,
          }}
        />
      </div>
    </div>
  );
}

const MEDIA_HEIGHT_MIN = 160;
const MEDIA_HEIGHT_MAX = 800;
const MEDIA_HEIGHT_DEFAULT = 280;

/** Returns embed URL for YouTube/Vimeo, or null if not a supported embed URL. */
function getEmbedVideoUrl(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim();
  // YouTube: watch, youtu.be, or already embed
  const ytMatch = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/i);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  // Vimeo: vimeo.com/ID or player.vimeo.com/video/ID
  const vimeoMatch = u.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/i);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
}

function MediaCard({ card, onDelete, onEdit, boardId, onSaveUrl, onSaveDisplayOptions, inlineUpload = true }) {
  const q = card?.queryDsl && typeof card.queryDsl === "object" ? card.queryDsl : {};
  const url = q.url || "";
  const mediaType = q.type || "image";
  const savedMediaHeight = Math.min(MEDIA_HEIGHT_MAX, Math.max(MEDIA_HEIGHT_MIN, Number(q.mediaHeight) || MEDIA_HEIGHT_DEFAULT));
  const [tab, setTab] = useState("upload");
  const [embedUrl, setEmbedUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [mediaHeight, setMediaHeight] = useState(savedMediaHeight);
  const [resizing, setResizing] = useState(false);
  const resizeStartRef = useRef({ y: 0, height: 0 });
  const fileInputRef = useRef(null);

  useEffect(() => {
    setMediaHeight(savedMediaHeight);
  }, [savedMediaHeight]);

  const handleResizerMouseDown = useCallback((e) => {
    e.preventDefault();
    setResizing(true);
    resizeStartRef.current = { y: e.clientY, height: mediaHeight };
  }, [mediaHeight]);

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const dy = e.clientY - resizeStartRef.current.y;
      const next = Math.min(MEDIA_HEIGHT_MAX, Math.max(MEDIA_HEIGHT_MIN, resizeStartRef.current.height + dy));
      setMediaHeight(next);
      resizeStartRef.current = { y: e.clientY, height: next };
    };
    const onUp = () => {
      setResizing(false);
      if (onSaveDisplayOptions) onSaveDisplayOptions(card?.id, { displaySize: "large", mediaHeight: resizeStartRef.current.height });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [resizing, card?.id, onSaveDisplayOptions]);

  const processFile = useCallback(async (file) => {
    if (!file || !boardId || !onSaveUrl) return;
    const isImage = file.type.startsWith("image/");
    if (file.type.startsWith("video/")) {
      setUploadError("Video files cannot be uploaded. Use Embed Video Link for YouTube or Vimeo.");
      return;
    }
    if (!isImage) {
      setUploadError("Please upload an image (JPEG, PNG, etc.).");
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const data = await analyticsApi.uploadBoardMedia(boardId, file);
      if (data?.url) onSaveUrl(data.url, "image");
    } catch (err) {
      setUploadError(err?.response?.data?.message || err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [boardId, onSaveUrl]);

  const handleFileChange = async (e) => {
    const file = e.target?.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.type.startsWith("video/")) {
      setUploadError("Video files cannot be dropped. Use Embed Video Link for YouTube or Vimeo.");
      return;
    }
    processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleEmbedBlur = () => {
    const v = (embedUrl || "").trim();
    if (v && onSaveUrl) onSaveUrl(v, v.match(/\.(mp4|webm|youtube|vimeo)/i) ? "video" : "image");
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 pr-6" style={{ backgroundColor: "transparent", paddingLeft: 0 }}>
      <div className="pb-4 flex-1 min-h-0 flex flex-col items-center justify-center" style={{ paddingLeft: 0 }}>
        {url ? (
          <div className="w-full flex flex-col items-center gap-3">
            {/* Resizable media area: drag the divider up/down to expand or reduce; separator visible on hover */}
            <div
              className="group/media w-full flex flex-col rounded overflow-hidden flex-shrink-0"
              style={{ backgroundColor: "#ffffff", height: mediaHeight }}
            >
              <div className="flex-1 flex items-center justify-center overflow-auto min-h-0">
                {getEmbedVideoUrl(url) ? (
                  <div className="flex items-center justify-center w-full h-full min-h-0">
                    <iframe
                      src={getEmbedVideoUrl(url)}
                      title={card?.title || "Video"}
                      className="rounded max-h-full w-full"
                      style={{ aspectRatio: "16/9", maxWidth: "100%", border: "none" }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : mediaType === "video" || url.match(/\.(mp4|webm)(\?|$)/i) ? (
                  <div className="flex items-center justify-center w-full">
                    <video
                      src={url}
                      controls
                      title={card?.title}
                      className="max-h-full w-auto max-w-full rounded"
                      style={{ display: "block" }}
                    />
                  </div>
                ) : (
                  <img
                    src={url}
                    alt={card?.title || "Media"}
                    className="object-contain rounded max-h-full w-auto max-w-full"
                    style={{ display: "block" }}
                  />
                )}
              </div>
              {/* Vertical resizer: visible on hover; drag up/down to change area height */}
              <div
                role="separator"
                aria-orientation="horizontal"
                onMouseDown={handleResizerMouseDown}
                className={`flex-shrink-0 h-2 cursor-ns-resize flex items-center justify-center hover:bg-black/20 active:bg-black/30 transition-all duration-200 ${resizing ? "opacity-100" : "opacity-0 group-hover/media:opacity-100"}`}
                style={{ backgroundColor: resizing ? theme.primaryLight + "40" : theme.border }}
                title="Drag to resize"
              >
                <div className="w-12 h-0.5 rounded-full opacity-60" style={{ backgroundColor: theme.textMuted }} />
              </div>
            </div>
          </div>
        ) : inlineUpload && boardId ? (
          <div className="w-full max-w-md">
            <div className="flex gap-4 mb-4">
              <button type="button" onClick={() => setTab("upload")} className="text-sm font-medium pb-1.5 border-b-2 -mb-px transition-colors" style={{ borderColor: tab === "upload" ? theme.textPrimary : "transparent", color: tab === "upload" ? theme.textPrimary : theme.textMuted }}>Upload Image</button>
              <button type="button" onClick={() => setTab("embed")} className="text-sm font-medium pb-1.5 border-b-2 -mb-px transition-colors" style={{ borderColor: tab === "embed" ? theme.textPrimary : "transparent", color: tab === "embed" ? theme.textPrimary : theme.textMuted }}>Embed Video Link</button>
            </div>
            {tab === "upload" ? (
              <div
                role="button"
                tabIndex={0}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
                onKeyDown={(e) => e.key === "Enter" && !uploading && fileInputRef.current?.click()}
                className="flex flex-col items-center justify-center py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors"
                style={{
                  borderColor: isDragging ? theme.primaryLight : (theme.borderMuted || theme.border),
                  backgroundColor: isDragging ? `${theme.primaryLight}10` : "transparent",
                }}
              >
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                {uploading ? (
                  <>
                    <Loader2 className="w-8 h-8 animate-spin mb-2" style={{ color: theme.primaryLight }} />
                    <p className="text-sm" style={{ color: theme.textMuted }}>Uploading...</p>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} className="px-3 py-1.5 rounded text-sm font-medium transition-colors" style={{ color: theme.primaryLight }}>Choose file</button>
                    <p className="text-xs mt-2" style={{ color: theme.textMuted }}>or drag and drop image</p>
                  </>
                )}
                {uploadError && <p className="text-xs mt-2" style={{ color: "#ef4444" }}>{uploadError}</p>}
              </div>
            ) : (
              <div>
                <input type="url" value={embedUrl} onChange={(e) => setEmbedUrl(e.target.value)} onBlur={handleEmbedBlur} placeholder="Paste image URL or YouTube/Vimeo embed link" className="w-full px-3 py-2 border rounded text-sm outline-none" style={{ borderColor: theme.border, backgroundColor: theme.bgBase, color: theme.textPrimary }} />
              </div>
            )}
          </div>
        ) : (
          <div className="text-center" style={{ color: theme.textMuted }}>
            <p className="text-sm">No media yet</p>
            {onEdit && <button type="button" onClick={onEdit} className="mt-1 text-xs underline" style={{ color: theme.primaryLight }}>Add image or embed video link</button>}
          </div>
        )}
      </div>
    </div>
  );
}

const SAVED_INDICATOR_MS = 2000;

export function BoardCard({ card, result, onSaveTextContent, onSaveMediaUrl, onSaveMediaDisplayOptions, boardId, rowHeightPx }) {
  const type = (card?.cardType || "insights").toLowerCase();
  switch (type) {
    case "insights":
      return <InsightsCard card={card} result={result} rowHeightPx={rowHeightPx} />;
    case "funnel":
      return <FunnelCard card={card} result={result} rowHeightPx={rowHeightPx} />;
    case "flow":
      return <FlowCard card={card} result={result} rowHeightPx={rowHeightPx} />;
    case "retention":
      return <ReportPlaceholderCard card={card} cardTypeLabel="Retention Report" />;
    case "text":
      return <TextCard card={card} onSaveContent={onSaveTextContent ? (payload) => onSaveTextContent(card?.id, payload) : undefined} inlineEdit={!!onSaveTextContent} />;
    case "media":
      return <MediaCard card={card} boardId={boardId} onSaveUrl={onSaveMediaUrl ? (url, mediaType) => onSaveMediaUrl(card?.id, url, mediaType) : undefined} onSaveDisplayOptions={onSaveMediaDisplayOptions} inlineUpload={!!onSaveMediaUrl} />;
    default:
      return <InsightsCard card={card} result={result} rowHeightPx={rowHeightPx} />;
  }
}

const INITIAL_BOARD_TIME = {
  mode: "default",
};

export default function BoardViewPage() {
  const { boardId, team } = useParams();
  const navigate = useNavigate();
  const selectedTeamData = JSON.parse(localStorage.getItem("selectedTeamData") || "{}");
  const teamId = selectedTeamData.id ?? selectedTeamData._id ?? null;
  const { board, loading: boardLoading, error: boardError, loadBoard, updateBoard } = useBoard(boardId, teamId);
  const [boardTime, setBoardTime] = useState(INITIAL_BOARD_TIME);
  const { cards, cardResults, loading: cardsLoading, deleteCard, executeAllCards, createCard, loadCards, updateCard } = useCards(boardId, teamId, boardTime);

  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [saveStatus, setSaveStatus] = useState("idle"); // 'idle' | 'saving' | 'saved'
  const [reorderSaving, setReorderSaving] = useState(false);
  const [orderedIds, setOrderedIds] = useState([]);
  /** Mixpanel-style: cursor-proximity drop intent (one line at a time) */
  const [dropIntent, setDropIntent] = useState(null);
  const [isDraggingCard, setIsDraggingCard] = useState(false);
  /** Manual save: buffer card changes (queryDsl, position) until user clicks Save */
  const [pendingChanges, setPendingChanges] = useState({}); // { [cardId]: { queryDsl?: {}, position?: {} } }
  const [orderDirty, setOrderDirty] = useState(false); // true when user reordered but not yet saved
  const savedTimeoutRef = useRef(null);
  const skipOrderedIdsSyncRef = useRef(false); // skip one sync after inserting card at position
  const rowContainerRefs = useRef({}); // rowKey -> DOM element for measuring row width when resizing
  const cardRefs = useRef({}); // cardId -> DOM element for cursor-proximity hit testing
  const draggingCardIdRef = useRef(null); // draggableId (string) of card being dragged; exclude from hit test
  const draggingSourceRowIndexRef = useRef(null); // source row index when drag started (for effective height during drag)
  const orderedRowsRef = useRef([]); // latest orderedRows for computeDropIntent in mousemove
  const [liveResizeWidths, setLiveResizeWidths] = useState({}); // { [rowKey]: { [cardId]: w } } during drag for smooth updates
  const liveResizeWidthsRef = useRef({}); // mirror for reading latest in onResizeEnd
  const liveRowHeightsRef = useRef({}); // { [rowKey]: h } during row height drag
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", confirmLabel: "Delete", cancelLabel: "Cancel", danger: true, onConfirm: null });
  const liveRowHeightsRafRef = useRef(null); // throttle live state to one update per frame
  const [liveRowHeights, setLiveRowHeights] = useState({}); // { [rowKey]: h } during drag for live card content updates
  const rowWrapperRefs = useRef({}); // rowKey -> DOM element (kept for optional DOM fallback)
  const [showShareModal, setShowShareModal] = useState(false);
  const [publicUrl, setPublicUrl] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  useEffect(() => {
    if (board) {
      setTitleValue(board.name || "Untitled");
      setDescriptionValue(board.description || "");
    }
  }, [board]);

  // Sync orderedIds from cards when list length changes (initial load, add, delete). Skip once after inserting at position.
  useEffect(() => {
    if (skipOrderedIdsSyncRef.current) {
      skipOrderedIdsSyncRef.current = false;
      return;
    }
    if (cards.length > 0 && (orderedIds.length === 0 || orderedIds.length !== cards.length)) {
      setOrderedIds(cards.map((c) => c.id));
    }
  }, [cards, orderedIds.length]);

  /** Normalize widths on load so each row's w sum = 1 (defensive for old/malformed data). */
  const normalizedCards = useMemo(() => normalizeCardsOnLoad(cards), [cards]);

  /** Working copy: normalized cards merged with pendingChanges for render (no DB round-trip). */
  const workingCards = useMemo(() => {
    const byId = Object.fromEntries(normalizedCards.map((c) => [c.id, c]));
    return normalizedCards.map((card) => {
      const pending = pendingChanges[card.id];
      if (!pending) return card;
      const baseQ = (card.queryDsl && typeof card.queryDsl === "object" ? card.queryDsl : {}) || {};
      const pendingQ = (pending.queryDsl && typeof pending.queryDsl === "object" ? pending.queryDsl : {}) || {};
      const basePos = (card.position && typeof card.position === "object" ? card.position : {}) || {};
      const pendingPos = (pending.position && typeof pending.position === "object" ? pending.position : {}) || {};
      return {
        ...card,
        queryDsl: { ...baseQ, ...pendingQ },
        position: { ...basePos, ...pendingPos },
      };
    });
  }, [normalizedCards, pendingChanges]);

  const isDirty = useMemo(
    () => Object.keys(pendingChanges).length > 0 || orderDirty,
    [pendingChanges, orderDirty]
  );

  const orderedCards = useMemo(() => {
    const byId = Object.fromEntries(workingCards.map((c) => [c.id, c]));
    return orderedIds.map((id) => byId[id]).filter(Boolean);
  }, [workingCards, orderedIds]);

  // Group cards by row for multi-column layout (position.rowIndex; fallback order = one card per row).
  // Use Number() so string "3" and number 3 from API never create separate rows.
  // Sort cards within each row by position.order so save order is stable (e.g. order 0, 1 not 1, 0).
  const orderedRows = useMemo(() => {
    const groups = new Map();
    orderedCards.forEach((card, index) => {
      const raw =
        typeof card.position?.rowIndex === "number"
          ? card.position.rowIndex
          : typeof card.position?.rowIndex === "string"
            ? parseInt(card.position.rowIndex, 10)
            : card.position?.order ?? index;
      const rowKey = Number.isFinite(raw) ? raw : index;
      if (!groups.has(rowKey)) groups.set(rowKey, []);
      groups.get(rowKey).push(card);
    });
    return Array.from(groups.entries())
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([, row]) => row.slice().sort((a, b) => (a.position?.order ?? 0) - (b.position?.order ?? 0)));
  }, [orderedCards]);

  // Row-level height map: key = rowKey string, value = h (height multiplier)
  const rowHeights = useMemo(() => {
    const map = {};
    orderedRows.forEach((row, rowIndex) => {
      if (!row || row.length === 0) return;
      const rowKey = row.map((c) => c.id).join("-");
      const h =
        typeof row[0]?.position?.h === "number" && Number.isFinite(row[0].position.h)
          ? Math.min(MAX_ROW_H, Math.max(MIN_ROW_H, row[0].position.h))
          : 1;
      map[rowKey] = h;
    });
    return map;
  }, [orderedRows]);

  useEffect(() => {
    orderedRowsRef.current = orderedRows;
  }, [orderedRows]);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    };
  }, []);

  // Execute all cards when they're loaded
  useEffect(() => {
    if (cards.length > 0 && Object.keys(cardResults).length === 0) {
      executeAllCards();
    }
  }, [cards, cardResults, executeAllCards]);

  const breadcrumbs = ["Boards", titleValue || board?.name || "Untitled"];

  const showSavedThenIdle = useCallback(() => {
    setSaveStatus("saved");
    if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    savedTimeoutRef.current = setTimeout(() => {
      setSaveStatus("idle");
      savedTimeoutRef.current = null;
    }, SAVED_INDICATOR_MS);
  }, []);

  const handleAddToBoardSelect = useCallback(
    (id, insertIndex, rowIdFromAdd) => {
      const bid = boardId || "new";
      // Row-specific add (right-side "+"): rowIdFromAdd is the canonical row id (same as row where + appears). Find that row and append.
      // Left-end add (new row): rowIdFromAdd is undefined; backend assigns next row via MAX(rowIndex)+1.
      const rowIdNum =
        typeof rowIdFromAdd === "number" && Number.isFinite(rowIdFromAdd) && rowIdFromAdd >= 0
          ? rowIdFromAdd
          : typeof rowIdFromAdd === "string"
            ? parseInt(rowIdFromAdd, 10)
            : undefined;
      const targetRow =
        rowIdNum !== undefined
          ? orderedRows.find((r) => {
              const raw = r[0]?.position?.rowIndex ?? r[0]?.position?.order;
              const id = typeof raw === "number" ? raw : parseInt(raw, 10);
              return Number.isFinite(id) && id === rowIdNum;
            })
          : null;
      const position =
        targetRow != null
          ? { rowIndex: rowIdNum, order: targetRow.length, w: 1 }
          : {};

      const isRowAdd = targetRow != null;

      switch (id) {
        case "insights": {
          const q = isRowAdd ? `?type=insights&rowIndex=${position.rowIndex}` : "?type=insights";
          navigate(`/${team}/analytics/boards/${bid}/editor${q}`);
          return;
        }
        case "funnels": {
          const q = isRowAdd ? `?type=funnels&rowIndex=${position.rowIndex}` : "?type=funnels";
          navigate(`/${team}/analytics/boards/${bid}/editor${q}`);
          return;
        }
        case "flows": {
          if (isRowAdd) {
            navigate(`/${team}/analytics/flow/${bid}?rowIndex=${position.rowIndex}`);
          } else {
            navigate(`/${team}/analytics/flow/${bid}`);
          }
          return;
        }
        case "retention": {
          const q = isRowAdd ? `?type=retention&rowIndex=${position.rowIndex}` : "?type=retention";
          navigate(`/${team}/analytics/boards/${bid}/editor${q}`);
          return;
        }
        case "text": {
          if (isRowAdd) {
            createCard({
              cardType: "text",
              title: "Text",
              queryDsl: { content: "" },
              visualization: {},
              position,
            })
              .then(() => loadCards())
              .catch((err) => console.error("Failed to create text card:", err));
            return;
          }
          skipOrderedIdsSyncRef.current = true;
          createCard({
            cardType: "text",
            title: "Text",
            queryDsl: { content: "" },
            visualization: {},
            position,
          })
            .then(async (newCard) => {
              const card = newCard?.data ?? newCard;
              const id = card?.id;
              if (!id || !boardId) {
                skipOrderedIdsSyncRef.current = false;
                return loadCards();
              }
              await loadCards();
              setOrderedIds((prev) => [...prev.slice(0, insertIndex), id, ...prev.slice(insertIndex)]);
              setOrderDirty(true);
            })
            .catch((err) => {
              skipOrderedIdsSyncRef.current = false;
              console.error("Failed to create text card:", err);
            });
          return;
        }
        case "media": {
          if (isRowAdd) {
            createCard({
              cardType: "media",
              title: "Media",
              queryDsl: { url: "", type: "image" },
              visualization: {},
              position,
            })
              .then(() => loadCards())
              .catch((err) => console.error("Failed to create media card:", err));
            return;
          }
          skipOrderedIdsSyncRef.current = true;
          createCard({
            cardType: "media",
            title: "Media",
            queryDsl: { url: "", type: "image" },
            visualization: {},
            position,
          })
            .then(async (newCard) => {
              const card = newCard?.data ?? newCard;
              const id = card?.id;
              if (!id || !boardId) {
                skipOrderedIdsSyncRef.current = false;
                return loadCards();
              }
              await loadCards();
              setOrderedIds((prev) => [...prev.slice(0, insertIndex), id, ...prev.slice(insertIndex)]);
              setOrderDirty(true);
            })
            .catch((err) => {
              skipOrderedIdsSyncRef.current = false;
              console.error("Failed to create media card:", err);
            });
          return;
        }
        default:
          break;
      }
    },
    [boardId, team, navigate, createCard, loadCards, orderedRows]
  );

  const handleResizeStartRowCards = useCallback(
    (rowKey, leftId, rightId, rowLength) => {
      const leftCard = workingCards.find((c) => c.id === leftId);
      const rightCard = workingCards.find((c) => c.id === rightId);
      if (!leftCard || !rightCard) return;
      const defaultW = 1 / Math.max(1, rowLength);
      const leftW = typeof leftCard.position?.w === "number" ? leftCard.position.w : defaultW;
      const rightW = typeof rightCard.position?.w === "number" ? rightCard.position.w : defaultW;
      liveResizeWidthsRef.current[rowKey] = { [leftId]: leftW, [rightId]: rightW };
      setLiveResizeWidths((prev) => ({ ...prev, [rowKey]: { [leftId]: leftW, [rightId]: rightW } }));
    },
    [workingCards]
  );

  const handleResizeRowCards = useCallback(
    (rowKey, leftId, rightId, deltaPx) => {
      const el = rowContainerRefs.current[rowKey];
      const rowWidth = el?.offsetWidth ?? 1;
      const fraction = Math.max(-1, Math.min(1, deltaPx / rowWidth));
      const row = liveResizeWidthsRef.current[rowKey] || {};
      if (row[leftId] == null || row[rightId] == null) return;
      let leftW = Math.max(MIN_CARD_WIDTH_FRACTION, Math.min(1 - MIN_CARD_WIDTH_FRACTION, row[leftId] + fraction));
      let rightW = Math.max(MIN_CARD_WIDTH_FRACTION, Math.min(1 - MIN_CARD_WIDTH_FRACTION, row[rightId] - fraction));
      liveResizeWidthsRef.current[rowKey] = { [leftId]: leftW, [rightId]: rightW };
      setLiveResizeWidths((prev) => ({ ...prev, [rowKey]: { [leftId]: leftW, [rightId]: rightW } }));
    },
    []
  );

  const handleResizeEndRowCards = useCallback((rowKey, leftId, rightId) => {
    const row = liveResizeWidthsRef.current[rowKey];
    if (row && row[leftId] != null && row[rightId] != null) {
      // Use widths as-is: -y on left, +y on right (no normalization).
      const leftW = Math.max(
        MIN_CARD_WIDTH_FRACTION,
        Math.min(1 - MIN_CARD_WIDTH_FRACTION, row[leftId])
      );
      const rightW = Math.max(
        MIN_CARD_WIDTH_FRACTION,
        Math.min(1 - MIN_CARD_WIDTH_FRACTION, row[rightId])
      );
      setPendingChanges((prev) => ({
        ...prev,
        [leftId]: { ...prev[leftId], position: { ...(prev[leftId]?.position || {}), w: leftW } },
        [rightId]: { ...prev[rightId], position: { ...(prev[rightId]?.position || {}), w: rightW } },
      }));
    }
    delete liveResizeWidthsRef.current[rowKey];
    setLiveResizeWidths((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }, []);

  // Row-level height change: update position.h for all cards in the row (rowIndex in orderedRows).
  const handleRowHeightChange = useCallback(
    (rowArrayIndex, newH) => {
      const clamped =
        Math.max(MIN_ROW_H, Math.min(MAX_ROW_H, Number.isFinite(newH) ? newH : 1));
      const row = orderedRows[rowArrayIndex] || [];
      if (row.length === 0) return;

      setPendingChanges((prev) => {
        const next = { ...prev };
        row.forEach((card) => {
          if (!card || card.id == null) return;
          const prevEntry = next[card.id] || {};
          const basePos =
            (prevEntry.position && typeof prevEntry.position === "object"
              ? prevEntry.position
              : (card.position && typeof card.position === "object"
                  ? card.position
                  : {})) || {};
          next[card.id] = {
            ...prevEntry,
            position: {
              ...basePos,
              h: clamped,
            },
          };
        });
        return next;
      });
    },
    [orderedRows]
  );

  const handleRowHeightResizeStart = useCallback((rowKey, currentH) => {
    const h = Math.max(MIN_ROW_H, Math.min(MAX_ROW_H, currentH ?? 1));
    liveRowHeightsRef.current[rowKey] = h;
    setLiveRowHeights((prev) => ({ ...prev, [rowKey]: h }));
  }, []);

  const handleRowHeightResize = useCallback((rowKey, deltaPx) => {
    const current = liveRowHeightsRef.current[rowKey] ?? 1;
    const currentRowHeightPx = BASE_ROW_HEIGHT_PX * current;
    const fraction = Math.max(-1, Math.min(1, -deltaPx / currentRowHeightPx));
    const newH = Math.max(MIN_ROW_H, Math.min(MAX_ROW_H, current + fraction));
    liveRowHeightsRef.current[rowKey] = newH;
    // Throttle React state to one update per frame so card content (charts, text) updates live without flooding
    if (liveRowHeightsRafRef.current == null) {
      liveRowHeightsRafRef.current = requestAnimationFrame(() => {
        liveRowHeightsRafRef.current = null;
        setLiveRowHeights((prev) => {
          const next = { ...prev };
          Object.keys(liveRowHeightsRef.current).forEach((k) => {
            next[k] = liveRowHeightsRef.current[k];
          });
          return next;
        });
      });
    }
  }, []);

  const handleRowHeightResizeEnd = useCallback((rowKey, rowArrayIndex) => {
    if (liveRowHeightsRafRef.current != null) {
      cancelAnimationFrame(liveRowHeightsRafRef.current);
      liveRowHeightsRafRef.current = null;
    }
    const finalH = liveRowHeightsRef.current[rowKey];
    if (finalH != null) handleRowHeightChange(rowArrayIndex, finalH);
    delete liveRowHeightsRef.current[rowKey];
    setLiveRowHeights((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }, [handleRowHeightChange]);

  const handleDeleteCard = (cardId) => {
    setConfirmDialog({
      open: true,
      title: "Delete chart",
      message: "Are you sure you want to delete this chart? This cannot be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true,
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        const card = workingCards.find((c) => c.id === cardId);
        const rowIndex = card?.position?.rowIndex ?? 0;
        const sameRowCards = workingCards.filter(
          (c) => c.id !== cardId && (c.position?.rowIndex ?? 0) === rowIndex
        );
        try {
          await deleteCard(cardId);
          setOrderedIds((prev) => prev.filter((id) => id !== cardId));
          setOrderDirty(true);
          setPendingChanges((prev) => {
            const next = { ...prev };
            delete next[cardId];
            if (sameRowCards.length > 0) {
              const rowH = sameRowCards[0]?.position?.h ?? 1;
              const w = 1 / sameRowCards.length;
              sameRowCards.forEach((c, order) => {
                next[c.id] = {
                  ...next[c.id],
                  position: {
                    rowIndex,
                    order,
                    w,
                    h: rowH,
                  },
                };
              });
            }
            return next;
          });
          await loadCards();
        } catch (err) {
          console.error("Failed to delete card:", err);
        }
      },
    });
  };

  /** Cursor-proximity hit test: return single drop intent (Mixpanel-style). Priority: top > bottom > left > right. */
  const computeDropIntent = useCallback((clientX, clientY) => {
    const rows = orderedRowsRef.current;
    const draggingId = draggingCardIdRef.current;
    if (!rows || rows.length === 0) return null;
    const zonePct = 0.28;
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex];
      for (let i = 0; i < row.length; i++) {
        const card = row[i];
        if (card == null || String(card.id) === draggingId) continue;
        const el = cardRefs.current[card.id];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) continue;
        const h = rect.height;
        const w = rect.width;
        if (clientY < rect.top + zonePct * h) return { type: "INSERT_ROW", position: "before", targetRowIndex: rowIndex };
        if (clientY > rect.bottom - zonePct * h) return { type: "INSERT_ROW", position: "after", targetRowIndex: rowIndex };
        if (clientX < rect.left + zonePct * w) return { type: "INSERT_COLUMN", position: "left", targetRowIndex: rowIndex, targetCardId: card.id };
        if (clientX > rect.right - zonePct * w) return { type: "INSERT_COLUMN", position: "right", targetRowIndex: rowIndex, targetCardId: card.id };
      }
    }
    const rowKeys = rows.map((r) => r.map((c) => c.id).join("-"));
    for (let r = 0; r < rows.length; r++) {
      const el = rowContainerRefs.current[rowKeys[r]];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const gapBottom = 48;
      if (clientY >= rect.bottom && clientY <= rect.bottom + gapBottom && clientX >= rect.left && clientX <= rect.right) return { type: "INSERT_ROW", position: "after", targetRowIndex: r };
    }
    return null;
  }, []);

  const dragListenersRef = useRef(null); // { remove: fn } to remove mousemove in handleDragEnd
  const boardScrollRef = useRef(null); // scrollable main for auto-scroll during drag
  const dropIntentRef = useRef(null); // last-shown purple line (only source of truth on drop)

  const DRAG_SCROLL_EDGE_PX = 80;
  const DRAG_SCROLL_SPEED = 14;

  const handleDragStart = useCallback((result) => {
    draggingCardIdRef.current = result.draggableId;
    setIsDraggingCard(true);
    const srcDroppable = String(result.source?.droppableId ?? "");
    draggingSourceRowIndexRef.current = parseInt(srcDroppable.replace("row-", ""), 10);
    if (Number.isNaN(draggingSourceRowIndexRef.current)) draggingSourceRowIndexRef.current = null;
    dropIntentRef.current = null;
    setDropIntent(null);
    const onMouseMove = (e) => {
      const intent = computeDropIntent(e.clientX, e.clientY);
      dropIntentRef.current = intent;
      setDropIntent(intent);
      const el = boardScrollRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        if (e.clientY < rect.top + DRAG_SCROLL_EDGE_PX) {
          el.scrollTop = Math.max(0, el.scrollTop - DRAG_SCROLL_SPEED);
        } else if (e.clientY > rect.bottom - DRAG_SCROLL_EDGE_PX) {
          el.scrollTop = Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + DRAG_SCROLL_SPEED);
        }
      }
    };
    window.addEventListener("mousemove", onMouseMove);
    dragListenersRef.current = {
      remove: () => {
        window.removeEventListener("mousemove", onMouseMove);
        dragListenersRef.current = null;
      },
    };
  }, [computeDropIntent]);

  const handleDragEnd = useCallback(
    (result) => {
      dragListenersRef.current?.remove?.();
      draggingCardIdRef.current = null;
      setIsDraggingCard(false);
      if (!boardId) {
        dropIntentRef.current = null;
        setDropIntent(null);
        return;
      }
      const sourceRowIndex = parseInt(String(result.source?.droppableId || "").replace("row-", ""), 10);
      if (Number.isNaN(sourceRowIndex) || sourceRowIndex < 0) {
        dropIntentRef.current = null;
        setDropIntent(null);
        return;
      }
      const rows = orderedRows.map((r) => r.map((c) => c.id));
      if (sourceRowIndex >= rows.length) {
        dropIntentRef.current = null;
        setDropIntent(null);
        return;
      }
      const sourceRow = rows[sourceRowIndex];
      const srcIdx = result.source?.index ?? -1;
      if (!sourceRow || srcIdx < 0 || srcIdx >= sourceRow.length) {
        dropIntentRef.current = null;
        setDropIntent(null);
        return;
      }
      const movedId = sourceRow[srcIdx];
      const sourceRowCopy = sourceRow.filter((_, i) => i !== srcIdx);
      const rowsWithoutMoved = rows.map((r, i) => (i === sourceRowIndex ? [...sourceRowCopy] : [...r]));
      // Use only the last-shown purple line (no cursor recompute)
      const intent = dropIntentRef.current ?? dropIntent;

      if (intent) {
        if (intent.type === "INSERT_ROW") {
          const R = intent.position === "before" ? intent.targetRowIndex : intent.targetRowIndex + 1;
          const newRows = [...rowsWithoutMoved.slice(0, R), [movedId], ...rowsWithoutMoved.slice(R)];
          const targetRowKey = (rowsWithoutMoved[intent.targetRowIndex] || []).join("-");
          const targetRowH = rowHeights[targetRowKey] ?? 1;
          const sourceRowKeyForHeight = sourceRow.join("-");
          const sourceRowH = rowHeights[sourceRowKeyForHeight] ?? 1;
          const sourceNewRowIndex = sourceRowIndex >= R ? sourceRowIndex + 1 : sourceRowIndex;
          setOrderedIds(newRows.flat());
          setOrderDirty(true);
          setPendingChanges((prev) => {
            let next = { ...prev, [movedId]: { ...prev[movedId], position: { rowIndex: R, order: 0, w: 1, h: targetRowH } } };
            if (sourceRowCopy.length > 0) {
              const sourceW = 1 / sourceRowCopy.length;
              sourceRowCopy.forEach((id, order) => {
                next = {
                  ...next,
                  [id]: { ...next[id], position: { rowIndex: sourceNewRowIndex, order, w: sourceW, h: sourceRowH } },
                };
              });
            }
            for (let r = R; r < rowsWithoutMoved.length; r++) {
              if (r === sourceRowIndex) continue;
              const rowKeyR = (rowsWithoutMoved[r] || []).join("-");
              const rowH = rowHeights[rowKeyR] ?? 1;
              for (const id of rowsWithoutMoved[r] || []) {
                if (id === movedId) continue;
                const card = workingCards.find((c) => c.id === id);
                const pos = (card?.position && typeof card.position === "object" ? card.position : {}) || {};
                const cur = (prev[id]?.position && typeof prev[id].position === "object" ? prev[id].position : {}) || pos;
                next = {
                  ...next,
                  [id]: { ...next[id], position: { rowIndex: r + 1, order: cur.order ?? 0, w: cur.w ?? 1, h: rowH } },
                };
              }
            }
            return next;
          });
        } else if (intent.type === "INSERT_COLUMN" && intent.targetCardId != null) {
          const targetRowIndex = intent.targetRowIndex;
          const targetRow = rowsWithoutMoved[targetRowIndex] || rows[targetRowIndex];
          if (targetRow) {
            const orderInRow = targetRow.indexOf(intent.targetCardId);
            const insertAt = intent.position === "left" ? orderInRow : orderInRow + 1;
            const newTargetRow = [...targetRow.slice(0, insertAt), movedId, ...targetRow.slice(insertAt)];
            const newRows = rowsWithoutMoved.map((r, i) => (i === targetRowIndex ? newTargetRow : r));
            const rowLen = newTargetRow.length;
            const w = 1 / rowLen;
            const targetRowKey = (rowsWithoutMoved[targetRowIndex] || []).join("-");
            const targetRowH = rowHeights[targetRowKey] ?? 1;
            const sourceRowKeyForHeight = sourceRow.join("-");
            const sourceRowH = rowHeights[sourceRowKeyForHeight] ?? 1;
            setOrderedIds(newRows.flat());
            setOrderDirty(true);
            setPendingChanges((prev) => {
              const next = { ...prev };
              newTargetRow.forEach((id, order) => {
                next[id] = {
                  ...next[id],
                  position: { rowIndex: targetRowIndex, order, w, h: targetRowH },
                };
              });
              if (sourceRowCopy.length > 0) {
                const sourceW = 1 / sourceRowCopy.length;
                sourceRowCopy.forEach((id, order) => {
                  const card = workingCards.find((c) => c.id === id);
                  const pos = (card?.position && typeof card.position === "object" ? card.position : {}) || {};
                  const cur = (prev[id]?.position && typeof prev[id].position === "object" ? prev[id].position : {}) || pos;
                  next[id] = {
                    ...next[id],
                    position: { rowIndex: sourceRowIndex, order, w: sourceW, h: sourceRowH },
                  };
                });
              }
              return next;
            });
          }
        }
        dropIntentRef.current = null;
        setDropIntent(null);
        return;
      }

      if (result.destination) {
        const { destination } = result;
        const destRowIndex = parseInt(String(destination.droppableId || "").replace("row-", ""), 10);
        if (!Number.isNaN(destRowIndex) && destRowIndex >= 0 && destRowIndex < rows.length) {
          const destRow = rows[destRowIndex];
          if (destRow) {
            const destRows = rows.map((r, i) => (i === sourceRowIndex ? [...sourceRowCopy] : [...r]));
            destRows[destRowIndex].splice(destination.index, 0, movedId);
            const newDestRow = destRows[destRowIndex];
            const rowLen = newDestRow.length;
            const w = 1 / rowLen;
            setOrderedIds(destRows.flat());
            setOrderDirty(true);
            const destRowKey = (rows[destRowIndex] || []).join("-");
            const targetRowH = rowHeights[destRowKey] ?? 1;
            const sourceRowKeyForHeight = sourceRow.join("-");
            const sourceRowH = rowHeights[sourceRowKeyForHeight] ?? 1;
            setPendingChanges((prev) => {
              const next = { ...prev };
              newDestRow.forEach((id, order) => {
                next[id] = {
                  ...next[id],
                  position: { rowIndex: destRowIndex, order, w, h: targetRowH },
                };
              });
              if (sourceRowIndex !== destRowIndex && sourceRowCopy.length > 0) {
                const sourceW = 1 / sourceRowCopy.length;
                sourceRowCopy.forEach((id, order) => {
                  const card = workingCards.find((c) => c.id === id);
                  const pos = (card?.position && typeof card.position === "object" ? card.position : {}) || {};
                  const cur = (prev[id]?.position && typeof prev[id].position === "object" ? prev[id].position : {}) || pos;
                  next[id] = {
                    ...next[id],
                    position: { rowIndex: sourceRowIndex, order, w: sourceW, h: sourceRowH },
                  };
                });
              }
              return next;
            });
          }
        }
      }
      dropIntentRef.current = null;
      setDropIntent(null);
    },
    [boardId, orderedRows, workingCards, dropIntent, rowHeights]
  );

  const editorTypeFromCardType = (cardType) => {
    const t = (cardType || "insights").toLowerCase();
    if (t === "funnel") return "funnels";
    if (t === "flow") return "flows";
    if (t === "retention") return "retention";
    return t;
  };

  const handleEditCard = (card) => {
    const type = editorTypeFromCardType(card?.cardType);
    // Flow cards use a dedicated editor page
    if (type === "flows") {
      navigate(`/${team}/analytics/flow/${boardId}?cardId=${card?.id || ""}`);
      return;
    }
    navigate(`/${team}/analytics/boards/${boardId}/editor?type=${type}&cardId=${card?.id || ""}`);
  };

  const handleSaveTextContent = useCallback((cardId, payload) => {
    const working = workingCards.find((c) => c.id === cardId);
    const existing = (working?.queryDsl && typeof working.queryDsl === "object" ? working.queryDsl : {}) || {};
    const nextQ =
      typeof payload === "string"
        ? { ...existing, content: payload || "" }
        : payload && typeof payload === "object"
          ? { ...existing, title: payload.title ?? "", content: payload.content ?? "", textStyle: payload.textStyle ?? "text" }
          : existing;
    setPendingChanges((prev) => ({ ...prev, [cardId]: { ...prev[cardId], queryDsl: nextQ } }));
  }, [workingCards]);

  const handleSaveMediaUrl = useCallback((cardId, url, type) => {
    const working = workingCards.find((c) => c.id === cardId);
    const existing = (working?.queryDsl && typeof working.queryDsl === "object" ? working.queryDsl : {}) || {};
    setPendingChanges((prev) => ({
      ...prev,
      [cardId]: { ...prev[cardId], queryDsl: { ...existing, url: url || "", type: type || "image" } },
    }));
  }, [workingCards]);

  const handleSaveMediaDisplayOptions = useCallback((cardId, payload) => {
    const working = workingCards.find((c) => c.id === cardId);
    const existing = (working?.queryDsl && typeof working.queryDsl === "object" ? working.queryDsl : {}) || {};
    setPendingChanges((prev) => ({
      ...prev,
      [cardId]: { ...prev[cardId], queryDsl: { ...existing, ...payload } },
    }));
  }, [workingCards]);

  /** Row height at index (for drag clone and on-drop: card must use target row h). */
  const getRowHeightAt = useCallback(
    (rowIndex) => {
      const row = orderedRows[rowIndex];
      if (!row || row.length === 0) return 1;
      const rowKey = row.map((c) => c.id).join("-");
      return rowHeights[rowKey] ?? 1;
    },
    [orderedRows, rowHeights]
  );

  /** Drag clone: render card at effective height (hover row or source row) so preview/placeholder match target. */
  const renderDragClone = useCallback(
    (provided, snapshot, rubric) => {
      const card = workingCards.find((c) => String(c.id) === rubric.draggableId);
      if (!card) return null;
      const sourceRowIndex = parseInt(String(rubric.source?.droppableId ?? "").replace("row-", ""), 10);
      const hoverRowIndex =
        dropIntent?.targetRowIndex != null ? dropIntent.targetRowIndex : !Number.isNaN(sourceRowIndex) ? sourceRowIndex : 0;
      const effectiveH = getRowHeightAt(hoverRowIndex);
      const heightPx = Math.round(BASE_ROW_HEIGHT_PX * effectiveH);
      const cardType = (card?.cardType || "insights").toLowerCase();
      const isBorderedType = cardType === "insights" || cardType === "funnel";
      const cardContent = (
        <div
          className={`rounded-xl overflow-hidden w-full relative pl-6 h-full flex-1 min-h-0 flex flex-col transition-all duration-200 ${isBorderedType ? "border border-gray-200 bg-white" : "border border-transparent bg-transparent"}`}
          style={isBorderedType ? {} : { backgroundColor: "transparent" }}
        >
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <BoardCard
              card={card}
              result={cardResults[card.id]}
              onSaveTextContent={handleSaveTextContent}
              onSaveMediaUrl={handleSaveMediaUrl}
              onSaveMediaDisplayOptions={handleSaveMediaDisplayOptions}
              boardId={boardId}
              rowHeightPx={heightPx}
            />
          </div>
        </div>
      );
      return (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className="min-w-0 flex flex-row items-stretch transition-shadow duration-200"
          style={{
            ...provided.draggableProps?.style,
            height: heightPx,
            minHeight: heightPx,
            opacity: 0.92,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          }}
        >
          <div className="min-w-0 flex-1 min-h-0 flex flex-col" style={{ height: "100%" }}>
            <BoardWidgetWithHandle
              card={card}
              cardIndex={workingCards.indexOf(card)}
              canEdit={["insights", "funnel", "flow", "retention", "text", "media"].includes((card.cardType || "").toLowerCase())}
              onDelete={() => handleDeleteCard(card.id)}
              onEdit={() => handleEditCard(card)}
              cardContent={cardContent}
              showDropIndicatorLeft={false}
              showDropIndicatorRight={false}
              provided={null}
              dragHandleProps={provided.dragHandleProps}
              snapshot={snapshot}
            />
          </div>
        </div>
      );
    },
    [
      workingCards,
      orderedRows,
      rowHeights,
      dropIntent,
      cardResults,
      getRowHeightAt,
      handleSaveTextContent,
      handleSaveMediaUrl,
      handleSaveMediaDisplayOptions,
      handleDeleteCard,
    ]
  );

  /** Flush pending card changes and order to the database; called when user clicks Save. */
  const handleGlobalSave = useCallback(async () => {
    if (!boardId || !isDirty) return;
    setSaveStatus("saving");
    try {
      const workingCardsById = Object.fromEntries(workingCards.map((c) => [c.id, c]));
      // Always persist canonical positions from current layout so order/rowIndex stay correct
      // (fixes duplicate order 0 when two cards share a row, and repairs after load)
      const positions = normalizeRowsForSave(orderedRows, workingCardsById);
      const savedPositionsById = Object.fromEntries(positions.map((p) => [p.id, p.position]));
      if (positions.length > 0) {
        await analyticsApi.updateCardPositions(boardId, positions, teamId);
      }
      for (const [cardId, pending] of Object.entries(pendingChanges)) {
        const baseCard = cards.find((c) => c.id === cardId);
        const working = workingCardsById[cardId];
        const baseQ = (baseCard?.queryDsl && typeof baseCard.queryDsl === "object" ? baseCard.queryDsl : {}) || {};
        const updates = {};
        if (pending.queryDsl && Object.keys(pending.queryDsl).length > 0) {
          updates.queryDsl = { ...baseQ, ...pending.queryDsl };
        }
        if (pending.position && Object.keys(pending.position).length > 0) {
          const pos = (working?.position && typeof working.position === "object" ? working.position : {}) || {};
          // When we just ran updateCardPositions, use the saved normalized position so we don't overwrite order/rowIndex with stale working copy
          const sourcePos = savedPositionsById?.[cardId] ?? pos;
          updates.position = {
            rowIndex: typeof sourcePos.rowIndex === "number" ? sourcePos.rowIndex : 0,
            order: typeof sourcePos.order === "number" ? sourcePos.order : 0,
            w:
              typeof pending.position.w === "number"
                ? pending.position.w
                : typeof sourcePos.w === "number"
                  ? sourcePos.w
                  : 1,
            h:
              typeof pending.position.h === "number"
                ? Math.max(MIN_ROW_H, Math.min(MAX_ROW_H, pending.position.h))
                : typeof sourcePos.h === "number"
                  ? Math.max(MIN_ROW_H, Math.min(MAX_ROW_H, sourcePos.h))
                  : 1,
          };
        }
        if (Object.keys(updates).length > 0) {
          await updateCard(cardId, updates);
        }
      }
      setPendingChanges({});
      setOrderDirty(false);
      const newCards = await loadCards();
      if (newCards && Array.isArray(newCards) && newCards.length > 0) {
        setOrderedIds(newCards.map((c) => c.id));
      }
      showSavedThenIdle();
    } catch (err) {
      console.error("Failed to save board changes:", err);
      setSaveStatus("idle");
    }
  }, [
    boardId,
    teamId,
    isDirty,
    orderDirty,
    pendingChanges,
    workingCards,
    orderedIds,
    orderedRows,
    cards,
    updateCard,
    loadCards,
    showSavedThenIdle,
  ]);

  const handleShareBoard = useCallback(async () => {
    const selectedTeamData = JSON.parse(localStorage.getItem("selectedTeamData") || "{}");
    const teamId = selectedTeamData.id ?? selectedTeamData._id ?? localStorage.getItem("selectedTeam");
    if (!teamId) {
      message.error("Team ID not found. Please select a team.");
      return;
    }
    if (!boardId) return;
    setShareLoading(true);
    try {
      const response = await analyticsApi.getBoardShareStatus(boardId, teamId);
      if (response?.data?.isPublic && response?.data?.publicUrl) {
        setPublicUrl(response.data.publicUrl);
        setShowShareModal(true);
      } else {
        const shareResponse = await analyticsApi.createBoardShare(boardId, teamId);
        if (shareResponse?.data?.publicUrl) {
          setPublicUrl(shareResponse.data.publicUrl);
          setShowShareModal(true);
        }
      }
    } catch (err) {
      console.error("Error getting share URL:", err);
      message.error("Failed to get share URL");
    } finally {
      setShareLoading(false);
    }
  }, [boardId]);

  const copyPublicUrl = useCallback(async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setUrlCopied(true);
      message.success("Public URL copied to clipboard!");
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (err) {
      console.error("Error copying URL:", err);
      message.error("Failed to copy URL");
    }
  }, [publicUrl]);

  const revokeBoardShare = useCallback(async () => {
    const selectedTeamData = JSON.parse(localStorage.getItem("selectedTeamData") || "{}");
    const teamId = selectedTeamData.id ?? selectedTeamData._id ?? localStorage.getItem("selectedTeam");
    if (!teamId || !boardId) return;
    setShareLoading(true);
    try {
      await analyticsApi.revokeBoardShare(boardId, teamId);
      setPublicUrl(null);
      setShowShareModal(false);
      message.success("Public access revoked");
    } catch (err) {
      console.error("Error revoking access:", err);
      message.error("Failed to revoke public access");
    } finally {
      setShareLoading(false);
    }
  }, [boardId]);

  const handleRenameBoard = useCallback(() => {
    setEditingTitle(true);
  }, []);

  const handleDeleteBoard = useCallback(() => {
    if (!boardId) return;
    setConfirmDialog({
      open: true,
      title: "Delete board",
      message: "Are you sure you want to delete this board? This cannot be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true,
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        try {
          await analyticsApi.deleteBoard(boardId);
          navigate(`/${team}/analytics/reports`);
        } catch (err) {
          console.error("Failed to delete board:", err);
        }
      },
    });
  }, [boardId, team, navigate]);

  const handleSaveBoard = useCallback(async () => {
    if (!board) return;
    const name = (titleValue || "").trim() || "Untitled";
    const description = (descriptionValue || "").trim() || null;
    const nameChanged = name !== (board.name || "Untitled");
    const descriptionChanged = (description ?? "") !== (board.description ?? "");
    if (!nameChanged && !descriptionChanged) return;
    setSaveStatus("saving");
    try {
      await updateBoard({ name, description: description ?? "" }, { silent: true });
      showSavedThenIdle();
    } catch (err) {
      console.error("Failed to save board:", err);
      setSaveStatus("idle");
    }
  }, [board, titleValue, descriptionValue, updateBoard, showSavedThenIdle]);

  /** Single Save action: flush card buffer then board meta. */
  const handleSaveClick = useCallback(async () => {
    if (isDirty) await handleGlobalSave();
    await handleSaveBoard();
  }, [isDirty, handleGlobalSave, handleSaveBoard]);

  const handleTitleChange = (e) => setTitleValue(e.target.value);

  const handleTitleBlur = () => {
    setEditingTitle(false);
    const name = (titleValue || "").trim() || "Untitled";
    setTitleValue(name);
  };

  const handleDescriptionChange = (e) => setDescriptionValue(e.target.value);

  const handleDescriptionBlur = () => {
    setEditingDescription(false);
    const description = (descriptionValue || "").trim() || null;
    setDescriptionValue(description ?? "");
  };

  const boardMetaDirty =
    !!board &&
    (((titleValue || "").trim() || "Untitled") !== (board.name || "Untitled") ||
      (descriptionValue ?? "").trim() !== (board.description ?? ""));
  const hasUnsavedChanges = isDirty || boardMetaDirty;

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  if (boardLoading) {
    return (
      <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: "#ffffff" }}>
        <AnalyticsTopBar breadcrumbs={breadcrumbs} showCreateNew={false} showTimeRange={false} />
        <TimeRangeFilters boardTime={boardTime} onBoardTimeChange={setBoardTime} />
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <div className="text-sm" style={{ color: theme.textMuted }}>
            Loading board...
          </div>
        </main>
      </div>
    );
  }

  if (boardError) {
    return (
      <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: "#ffffff" }}>
        <AnalyticsTopBar breadcrumbs={breadcrumbs} showCreateNew={false} showTimeRange={false} />
        <TimeRangeFilters boardTime={boardTime} onBoardTimeChange={setBoardTime} />
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <div className="text-sm" style={{ color: "#ef4444" }}>
            Error: {boardError}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: "#ffffff" }}>
      <ConfirmModal
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel={confirmDialog.cancelLabel}
        danger={confirmDialog.danger}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((d) => ({ ...d, open: false }))}
      />
      <AnalyticsTopBar
        breadcrumbs={breadcrumbs}
        showCreateNew={false}
        showTimeRange={false}
        showSaveClose={true}
        saveOnly={true}
        onSave={handleSaveClick}
        saveStatus={saveStatus}
        reorderSaving={reorderSaving}
        saveDisabled={!hasUnsavedChanges}
        showBoardActions={true}
        onShare={handleShareBoard}
        onDelete={handleDeleteBoard}
        onRename={handleRenameBoard}
      />
      <TimeRangeFilters boardTime={boardTime} onBoardTimeChange={setBoardTime} />
      <main ref={boardScrollRef} className="flex-1 min-w-0 overflow-auto pl-4 pr-6 py-4" style={{ backgroundColor: "#ffffff", fontFamily: "Raleway, sans-serif" }}>
        <div className="w-full min-w-0 max-w-full">
          {/* Board title & description - aligned with card content (pl-6 matches card drag-handle offset) */}
          <div className="mb-4 pl-6 pr-6">
            {editingTitle ? (
              <input
                type="text"
                value={titleValue}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleTitleBlur();
                  if (e.key === "Escape") {
                    setTitleValue(board?.name || "Untitled");
                    setEditingTitle(false);
                  }
                }}
                autoFocus
                className="w-full px-0 py-0.5 border-0 border-b rounded-none outline-none bg-transparent text-xl font-semibold"
                style={{
                  color: theme.textPrimary,
                  borderColor: theme.border,
                  fontFamily: "Raleway, sans-serif"
                }}
              />
            ) : (
              <h1
                className="text-xl font-semibold cursor-pointer py-0.5 -mx-0 rounded hover:opacity-80 transition-opacity"
                style={{ color: theme.textPrimary }}
                onClick={() => setEditingTitle(true)}
                title="Click to edit"
              >
                {titleValue || board?.name || "Untitled"}
              </h1>
            )}
            {editingDescription ? (
              <textarea
                value={descriptionValue}
                onChange={handleDescriptionChange}
                onBlur={handleDescriptionBlur}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setDescriptionValue(board?.description || "");
                    setEditingDescription(false);
                  }
                }}
                autoFocus
                placeholder="+ Add description..."
                rows={2}
                className="text-sm w-full mt-0.5 px-0 py-0.5 border-0 rounded outline-none resize-none bg-transparent"
                style={{
                  color: descriptionValue ? theme.textSecondary : theme.textMuted,
                  fontFamily: "Raleway, sans-serif"
                }}
              />
            ) : (
              <p
                className="text-sm mt-0.5 cursor-pointer py-0.5 min-h-[1.25rem] rounded hover:opacity-80 transition-opacity"
                style={{ color: descriptionValue ? theme.textSecondary : theme.textMuted }}
                onClick={() => setEditingDescription(true)}
                title="Click to edit"
              >
                {descriptionValue || "+ Add description..."}
              </p>
            )}
            {board && (
              <p
                className="text-xs mt-0.5"
                style={{ color: theme.textMuted }}
              >
                Created by{" "}
                {board.createdByUserName ||
                  board.creatorName ||
                  board.createdByUserEmail ||
                  board.creatorEmail ||
                  "Unknown"}
                {board.createdAt && (
                  <> • {new Date(board.createdAt).toLocaleDateString()}</>
                )}
              </p>
            )}
          </div>

          {/* Drag-and-drop list: one Droppable per row so direct children are Draggables (required by @hello-pangea/dnd) */}
          <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="flex flex-col w-full gap-3" style={{ padding: 0 }}>
              {orderedRows.map((row, rowIndex) => {
                const rowKey = row.map((c) => c.id).join("-");
                const rowIndexVal = row[0]?.position?.rowIndex ?? row[0]?.position?.order ?? rowIndex;
                const rowId = Number.isFinite(Number(rowIndexVal)) ? Number(rowIndexVal) : rowIndex;
                const isMultiCardRow = row.length > 1;
                const defaultW = 1 / row.length;
                const rowHeightMultiplier = liveRowHeights[rowKey] ?? rowHeights[rowKey] ?? 1;
                const rowHeightPx = BASE_ROW_HEIGHT_PX * rowHeightMultiplier;
                const showLineAbove =
                  dropIntent?.type === "INSERT_ROW" && dropIntent?.position === "before" && dropIntent?.targetRowIndex === rowIndex;
                const showLineBelow =
                  dropIntent?.type === "INSERT_ROW" && dropIntent?.position === "after" && dropIntent?.targetRowIndex === rowIndex;
                return (
                  <div key={rowKey} className="flex flex-col gap-1">
                    <div
                      ref={(el) => {
                        rowWrapperRefs.current[rowKey] = el;
                      }}
                      style={{ height: rowHeightPx, minHeight: rowHeightPx, maxHeight: rowHeightPx, overflow: "hidden", flexShrink: 0, flexGrow: 0 }}
                      className="flex flex-col"
                    >
                      <BoardRow
                        rowIndex={rowId}
                        rowArrayIndex={rowIndex}
                        insertIndex={orderedRows.flat().length}
                        onAddSelect={handleAddToBoardSelect}
                        rowContainerRef={(el) => {
                          rowContainerRefs.current[rowKey] = el;
                        }}
                        showLineAbove={showLineAbove}
                        showLineBelow={showLineBelow}
                        rowClassName="flex-1 min-h-0"
                      >
                      <Droppable droppableId={`row-${rowIndex}`} isDropDisabled={true} renderClone={renderDragClone}>
                        {(droppableProvided) => (
                          <div
                            ref={(el) => {
                              droppableProvided.innerRef(el);
                              rowContainerRefs.current[rowKey] = el;
                            }}
                            {...droppableProvided.droppableProps}
                            className="board-droppable-row flex-1 min-w-0 min-h-0 flex flex-row items-stretch pr-px rounded-lg"
                          >
                            {row.map((card, orderInRow) => {
                            const cardType = (card?.cardType || "insights").toLowerCase();
                            const isText = cardType === "text";
                            const isBorderedType = cardType === "insights" || cardType === "funnel" || cardType === "retention";
                            const canEdit = ["insights", "funnel", "flow", "retention", "text", "media"].includes((card.cardType || "").toLowerCase());
                            // Row total = ROW_TOTAL_W; single-card rows always get full width (1); multi-card use stored w or default.
                            const cardW = row.length === 1 ? 1 : (liveResizeWidths[rowKey]?.[card.id] ?? (typeof card.position?.w === "number" ? card.position.w : defaultW));
                            const cardContent = (
                              <div
                                className={`rounded-xl overflow-hidden w-full relative pl-6 h-full flex-1 min-h-0 flex flex-col transition-all duration-200 ${isBorderedType ? "border border-gray-200 bg-white group-hover:shadow-sm group-hover:bg-[#fafbfc]" : "border border-transparent bg-transparent"}`}
                                style={{
                                  ...(isBorderedType ? {} : { backgroundColor: "transparent" }),
                                }}
                              >
                                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                                  <BoardCard
                                    card={card}
                                    result={cardResults[card.id]}
                                    onSaveTextContent={handleSaveTextContent}
                                    onSaveMediaUrl={handleSaveMediaUrl}
                                    onSaveMediaDisplayOptions={handleSaveMediaDisplayOptions}
                                    boardId={boardId}
                                    rowHeightPx={rowHeightPx}
                                  />
                                </div>
                              </div>
                            );
                            return (
                              <Draggable key={card.id} draggableId={String(card.id)} index={orderInRow}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={(el) => {
                                      provided.innerRef(el);
                                      if (el) cardRefs.current[card.id] = el;
                                    }}
                                    {...provided.draggableProps}
                                    className="min-w-0 h-full flex flex-row items-stretch transition-shadow duration-200"
                                    style={{
                                      ...provided.draggableProps?.style,
                                      flexBasis: 0,
                                      flexGrow: cardW,
                                      flexShrink: cardW,
                                      minWidth: 80,
                                      opacity: snapshot?.isDragging ? 0.92 : 1,
                                      ...(snapshot?.isDragging
                                        ? {
                                            zIndex: 1000,
                                            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                                          }
                                        : {}),
                                    }}
                                  >
<div className="min-w-0 flex-1 min-h-0 flex flex-col">
                                        <BoardWidgetWithHandle
                                        card={card}
                                        cardIndex={orderedRows.flat().indexOf(card)}
                                        canEdit={canEdit}
                                        onDelete={() => handleDeleteCard(card.id)}
                                        onEdit={() => handleEditCard(card)}
                                        cardContent={cardContent}
                                        showDropIndicatorLeft={
                                          dropIntent?.type === "INSERT_COLUMN" &&
                                          dropIntent?.position === "left" &&
                                          dropIntent?.targetCardId === card.id
                                        }
                                        showDropIndicatorRight={
                                          dropIntent?.type === "INSERT_COLUMN" &&
                                          dropIntent?.position === "right" &&
                                          dropIntent?.targetCardId === card.id
                                        }
                                        provided={null}
                                        dragHandleProps={provided.dragHandleProps}
                                        snapshot={snapshot}
                                      />
                                    </div>
                                    {orderInRow < row.length - 1 && (
                                      <RowWidthResizer
                                        onResizeStart={() =>
                                          handleResizeStartRowCards(rowKey, card.id, row[orderInRow + 1].id, row.length)
                                        }
                                        onResize={(deltaPx) =>
                                          handleResizeRowCards(rowKey, card.id, row[orderInRow + 1].id, deltaPx)
                                        }
                                        onResizeEnd={() =>
                                          handleResizeEndRowCards(rowKey, card.id, row[orderInRow + 1].id)
                                        }
                                      />
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            );
                            })}
                            <div
                              className={isDraggingCard ? "flex-[0_0_auto] min-w-0 shrink-0" : "flex-[0_0_0] min-w-0 max-w-0 overflow-hidden shrink-0"}
                              aria-hidden={!isDraggingCard}
                            >
                              {droppableProvided.placeholder}
                            </div>
                          </div>
                        )}
                      </Droppable>
                    </BoardRow>
                    </div>
                    {/* Row height resizer: drag handle like column resizer */}
                    <div className="w-full pl-6 pr-6 flex justify-center">
                      <RowHeightResizer
                        onResizeStart={() => handleRowHeightResizeStart(rowKey, rowHeightMultiplier)}
                        onResize={(deltaPx) => handleRowHeightResize(rowKey, deltaPx)}
                        onResizeEnd={() => handleRowHeightResizeEnd(rowKey, rowIndex)}
                      />
                    </div>
                  </div>
                );
              })}
              <AddContentBlock insertIndex={orderedRows.length} onSelect={handleAddToBoardSelect} />
            </div>
          </DragDropContext>

          <p className="mt-10 text-xs" style={{ color: theme.textMuted }}>
            Need help?{" "}
            <span className="cursor-pointer hover:underline" style={{ color: theme.primaryLight }}>
              View Demo Boards
            </span>
          </p>
        </div>
      </main>

      {/* Share Board Modal */}
      <Modal
        title="Share Board"
        open={showShareModal}
        onCancel={() => {
          setShowShareModal(false);
          setUrlCopied(false);
        }}
        footer={[
          publicUrl ? (
            <Button key="revoke" danger onClick={revokeBoardShare} loading={shareLoading}>
              Revoke Access
            </Button>
          ) : null,
          <Button key="close" onClick={() => { setShowShareModal(false); setUrlCopied(false); }}>
            Close
          </Button>
        ]}
      >
        <div style={{ padding: "16px 0" }}>
          <Text style={{ color: theme.textSecondary, fontSize: 14, display: "block", marginBottom: 16 }}>
            Anyone with this link can view your board. You can revoke access at any time.
          </Text>
          {publicUrl ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", padding: 12, backgroundColor: "#f5f5f5", borderRadius: 4, border: "1px solid #DDE2EE" }}>
              <Input value={publicUrl} readOnly style={{ fontFamily: "Outfit, sans-serif", fontSize: 13, flex: 1 }} />
              <Button onClick={copyPublicUrl} style={{ fontFamily: "Outfit, sans-serif", fontWeight: 500, borderColor: "#1d0c46", color: "#1d0c46" }}>
                {urlCopied ? "Copied!" : "Copy"}
              </Button>
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: 24 }}>
              {shareLoading ? "Loading..." : null}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
